import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { api, fmt } from "../../../services/erpApi";
import { PageHeader, LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import EmployeeAvatar from "../../../components/admin/EmployeeAvatar";
import { useToast } from "../../../components/admin/ToastContext";
import {
  Plus, Search, Eye, Pencil, Trash2, Target, TrendingUp, Banknote, Gauge,
  LayoutGrid, List, Download, UserPlus, GitMerge, Split, FileText, Check, X, CalendarClock, CalendarDays, Mail,
  Activity, History, Paperclip, Upload, CalendarPlus, BellRing, MessageSquare, MoreVertical, RotateCcw,
  Package, Inbox, Calendar, Phone, MessageCircle,
} from "lucide-react";

const PAGE_SIZE = 20;

const norm = (r) => (r && typeof r === "object" && !Array.isArray(r) && "data" in r ? r.data : r);

const PRIORITY_LABELS = { 0: "Low", 1: "Medium", 2: "High", 3: "Urgent" };
const PRIORITY_COLORS = { 0: "#64748b", 1: "#0f766e", 2: "#b45309", 3: "#be123c" };

const AVATAR_BG = [
  "#4c6a4f", "#5b6b3f", "#3f6b63", "#6b5b3f", "#4f5b6b", "#6b4f5b",
  "#5a6b3f", "#3f5b6b", "#6b6b4f", "#4f6a6b", "#6b4f6b", "#5b4f6b",
];
const getInitials = (n) => (n || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

/* PIVOT + GRAPH CONSTANTS — temporarily disabled (CRM Dashboard already covers these).
const DIM_LABELS = { stage: "Stage", type: "Type", team: "Team", owner: "Owner", source: "Source", month: "Month" };
const MEASURE_LABELS = { count: "Count", exp: "Expected revenue", won: "Won revenue", wonCount: "Won count" };
const CHART_TYPE_LABELS = { bar: "Bar", donut: "Donut", line: "Line" };
const CHART_COLORS = ["#225f2b", "#0f766e", "#b45309", "#be123c", "#4c6a4f", "#6b5b3f", "#3f6b63", "#475569"];
*/

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
  fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
};
const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };

const FormField = ({ label, children, required }) => (
  <div style={{ marginBottom: "14px" }}>
    <label style={labelStyle}>{label}{required && <span style={{ color: "#be123c" }}> *</span>}</label>
    {children}
  </div>
);

const DetailField = ({ label, children, full }) => (
  <div style={{ background: "#fafbfa", border: "1px solid #f1f3f0", borderRadius: 8, padding: "10px 12px", gridColumn: full ? "1 / -1" : undefined }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".02em", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 13.5, color: "#1e293b", fontWeight: 500, lineHeight: 1.5, wordBreak: "break-word" }}>{children ?? "—"}</div>
  </div>
);

const KpiCard = ({ label, value, icon: Icon, trend, active, onClick }) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    style={{
      background: active ? "#f0f7f0" : "#fff",
      border: active ? "2px solid #225f2b" : "1px solid #e2e7e2",
      borderRadius: 8, padding: "8px 12px", textAlign: "left",
      cursor: onClick ? "pointer" : "default", fontFamily: "inherit",
      transition: "background .15s", minWidth: 0, minHeight: 0,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: active ? "#225f2b" : "#64748b", display: "inline-flex", flexShrink: 0 }}>
        {Icon && <Icon size={13} />}
      </span>
      <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: ".02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</p>
      {active && <span style={{ marginLeft: "auto", fontSize: 9, fontWeight: 700, color: "#225f2b", background: "#eef6f3", padding: "1px 6px", borderRadius: 10, flexShrink: 0 }}>ACTIVE</span>}
    </div>
    <p style={{ fontSize: 18, fontWeight: 600, color: "#0f172a", lineHeight: 1.2, marginTop: 4 }}>{value ?? "—"}</p>
    {trend && <p style={{ fontSize: 10, color: "#94a3b8", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{trend}</p>}
  </button>
);

const typeBadge = (t) =>
  t === "opportunity"
    ? { text: "#225f2b", bg: "#f0f7f0", label: "OPPORTUNITY" }
    : { text: "#b45309", bg: "#fffbeb", label: "LEAD" };

const stageStyle = (stage) => {
  const name = String((stage && (stage.name || stage.stage_name)) || "").toLowerCase();
  if (stage?.is_won || name.includes("won") || name.includes("gagn")) return { text: "#225f2b", bg: "#f0f7f0" };
  if (stage?.is_lost || name.includes("lost") || name.includes("perd")) return { text: "#be123c", bg: "#fff1f2" };
  return { text: "#334155", bg: "#eef2f7" };
};

const SearchSelect = ({ id, options, value, placeholder = "Select…", emptyLabel, onChange, avatarField }) => {
  const [val, setVal] = useState(value);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => setVal(value), [value]);
  const selected = options.find((o) => String(o.value) === String(val));
  const filtered = q.trim()
    ? options.filter((o) => `${o.label} ${o.sub || ""}`.toLowerCase().includes(q.toLowerCase()))
    : options;
  const pick = (v) => { setVal(v); setOpen(false); setQ(""); if (onChange) onChange(v); };
  const avatarOf = (o) => avatarField ? o[avatarField] : null;
  return (
    <div style={{ position: "relative" }}>
      <input type="hidden" id={id} value={val ?? ""} />
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ ...inputStyle, cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
          {selected && avatarOf(selected) && (
            <span style={{ width: 20, height: 20, borderRadius: "50%", background: AVATAR_BG[(Number(avatarOf(selected)?.id) || 0) % AVATAR_BG.length], display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 600, color: "white", flexShrink: 0 }}>
              {getInitials(avatarOf(selected)?.name)}
            </span>
          )}
          <span style={{ color: selected ? "#1e293b" : "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {selected ? selected.label : placeholder}
          </span>
        </span>
        <span style={{ color: "#94a3b8", fontSize: 11 }}>▾</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 41, background: "#fff", border: "1px solid #e2e7e2", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", marginTop: 4, overflow: "hidden" }}>
            <div style={{ padding: 8, borderBottom: "1px solid #f1f3f0", position: "relative" }}>
              <Search size={13} style={{ position: "absolute", left: 18, top: 16, color: "#94a3b8" }} />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" style={{ ...inputStyle, padding: "6px 10px 6px 28px", fontSize: 12.5 }} />
            </div>
            <div className="no-scrollbar" style={{ maxHeight: 190, overflowY: "auto", padding: "4px 0" }}>
              {emptyLabel !== undefined && (
                <button type="button" onClick={() => pick("")} style={menuItem}>{emptyLabel}</button>
              )}
              {filtered.length === 0 && <div style={{ padding: 12, fontSize: 12, color: "#94a3b8", textAlign: "center" }}>No results</div>}
              {filtered.map((o) => (
                <button key={o.value} type="button" onClick={() => pick(String(o.value))} style={{ ...menuItem, background: String(o.value) === String(val) ? "#f0f7f0" : "transparent", display: "flex", alignItems: "center", gap: 8 }}>
                  {avatarOf(o) && (
                    <span style={{ width: 22, height: 22, borderRadius: "50%", background: AVATAR_BG[(Number(avatarOf(o)?.id) || 0) % AVATAR_BG.length], display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 600, color: "white", flexShrink: 0 }}>
                      {getInitials(avatarOf(o)?.name)}
                    </span>
                  )}
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: "block", fontWeight: 600 }}>{o.label}</span>
                    {o.sub && <span style={{ display: "block", fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>{o.sub}</span>}
                  </span>
                  {String(o.value) === String(val) && <Check size={13} style={{ marginLeft: "auto", color: "#225f2b", flexShrink: 0 }} />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const AdminPanelCrmPage = () => {
  const toast = useToast();
  const permissions = useSelector((s) => s.auth?.permissions);
  const canManage = !!(permissions?.canManageCRM || permissions?.canManageCrm || permissions?.isAdmin);

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("kanban");
  const [showStats, setShowStats] = useState(false);
  const [calDate, setCalDate] = useState(() => new Date());
  /* PIVOT + GRAPH STATES — temporarily disabled (CRM Dashboard already covers these).
  const [pivDim, setPivDim] = useState("stage");
  const [pivCol, setPivCol] = useState("none");
  const [pivMeasure, setPivMeasure] = useState("count");
  const [graphType, setGraphType] = useState("bar");
  const [graphMeasure, setGraphMeasure] = useState("count");
  const [graphGroup, setGraphGroup] = useState("stage");
  useEffect(() => {
    if (graphType === "line" && graphGroup !== "month") setGraphGroup("month");
  }, [graphType, graphGroup]);
  */

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [sortBy, setSortBy] = useState("date_open");
  const [sortDir, setSortDir] = useState("desc");

  const [stages, setStages] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [sources, setSources] = useState([]);
  const [mediums, setMediums] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [tags, setTags] = useState([]);
  const [lostReasons, setLostReasons] = useState([]);

  const [dash, setDash] = useState(null);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState("overview");
  const [history, setHistory] = useState([]);

  const [activityTypes, setActivityTypes] = useState([]);
  const [newActivity, setNewActivity] = useState({ activity_type_id: "", user_id: "", date_deadline: "", summary: "" });
  const [newMessage, setNewMessage] = useState({ body: "", type: "note" });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ tag_ids: [] });

  const [actionTarget, setActionTarget] = useState(null); // { action, lead }
  const [actionBusy, setActionBusy] = useState(false);
  const [quotPreview, setQuotPreview] = useState(null);
  const [quotPreviewLoading, setQuotPreviewLoading] = useState(false);
  const [createdQuotation, setCreatedQuotation] = useState(null);
  const [quotSendBusy, setQuotSendBusy] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [mergeTarget, setMergeTarget] = useState(null); // { principal }

  const [mailOpen, setMailOpen] = useState(false);
  const [mailTemplates, setMailTemplates] = useState([]);
  const [mailTemplateId, setMailTemplateId] = useState("");
  const [mailBusy, setMailBusy] = useState(false);

  const [waBulkOpen, setWaBulkOpen] = useState(false);
  const [waBulkTemplates, setWaBulkTemplates] = useState([]);
  const [waBulkTemplateId, setWaBulkTemplateId] = useState("");
  const [waBulkBusy, setWaBulkBusy] = useState(false);

  const [detailProducts, setDetailProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [addingProduct, setAddingProduct] = useState(false);

  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [openActions, setOpenActions] = useState(null);

  useEffect(() => { setPage(1); }, [search, typeFilter, stageFilter, teamFilter, ownerFilter, priorityFilter, includeInactive, view]);

  const filters = useMemo(() => ({
    search: search.trim() || undefined,
    type: typeFilter !== "all" ? typeFilter : undefined,
    stage_id: stageFilter !== "all" ? stageFilter : undefined,
    team_id: teamFilter !== "all" ? teamFilter : undefined,
    user_id: ownerFilter !== "all" ? ownerFilter : undefined,
    priority: priorityFilter !== "all" ? priorityFilter : undefined,
    include_inactive: includeInactive ? true : undefined,
  }), [search, typeFilter, stageFilter, teamFilter, ownerFilter, priorityFilter, includeInactive]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const all = [];
      let pageNo = 1;
      const limit = 100;
      for (let guard = 0; guard < 20; guard++) {
        const res = await api.crm.list({ ...filters, page: pageNo, limit });
        const rows = res && typeof res === "object" && !Array.isArray(res) && "data" in res ? res.data : res;
        const arr = Array.isArray(rows) ? rows : [];
        all.push(...arr);
        const total = typeof res?.total === "number" ? res.total : arr.length;
        if (arr.length === 0 || all.length >= total || arr.length < limit) break;
        pageNo += 1;
      }
      setLeads(all);
      setTotal(all.length);
    } catch (e) {
      toast(e.message || "Load failed", "error");
      setLeads([]);
    }
    setLoading(false);
  }, [filters, toast]);

const loadReferentials = useCallback(async () => {
    const norm = (r) => (r && typeof r === "object" && !Array.isArray(r) && "data" in r ? r.data : r);
    const [s, t, u, p, src, med, camp, tg, lr, at] = await Promise.all([
      api.crm.stages.list({ limit: 100 }).catch(() => []),
      api.crm.teams.list({ limit: 100 }).catch(() => []),
      api.users.list().catch(() => []),
      api.partners.list({ limit: 500 }).catch(() => []),
      api.crm.sources.list({ limit: 100 }).catch(() => []),
      api.crm.mediums.list({ limit: 100 }).catch(() => []),
      api.crm.campaigns.list({ limit: 100 }).catch(() => []),
      api.crm.tags.list({ limit: 100 }).catch(() => []),
      api.crm.lostReasons.list({ limit: 100 }).catch(() => []),
      api.crm.activityTypes.list({ limit: 100 }).catch(() => []),
    ]);
    const sortStages = (rows) => norm(rows || []).slice().sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    setStages(sortStages(s));
    setTeams(norm(t || []));
    setUsers(norm(u || []));
    setPartners(norm(p || []));
    setSources(norm(src || []));
    setMediums(norm(med || []));
    setTags(norm(tg || []));
    setLostReasons(norm(lr || []));
    setActivityTypes(norm(at || []));
  }, []);

  useEffect(() => {
    loadReferentials();
    setLoaded(true);
  }, [loadReferentials]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.products.list({ limit: 500, active: true });
        const n = norm(res);
        setAllProducts(Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : []);
      } catch { setAllProducts([]); }
    })();
  }, []);

  useEffect(() => {
    api.crm.dashboard()
      .then((r) => setDash(norm(r)))
      .catch(() => setDash(null));
  }, []);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const q = sp.get("q");
    const v = sp.get("view");
    const st = sp.get("stage_id");
    const u = sp.get("user_id");
    const t = sp.get("team_id");
    const tp = sp.get("type");
    if (q) setSearch(q);
    if (v) setView(v);
    if (st) setStageFilter(st);
    if (u) setOwnerFilter(u);
    if (t) setTeamFilter(t);
    if (tp) setTypeFilter(tp);
  }, []);

  useEffect(() => {
    if (loaded) loadLeads();
  }, [loadLeads, loaded]);

  const columns = useMemo(() => stages.filter((s) => s.is_active !== false), [stages]);

  const stageById = useMemo(() => {
    const m = new Map();
    stages.forEach((s) => m.set(String(s.id), s));
    return m;
  }, [stages]);

  const stageFor = useCallback((l) => stageById.get(String(l.stage_id)), [stageById]);

  const userOf = useCallback((id) => {
    if (id === null || id === undefined || id === "") return null;
    return users.find((x) => String(x.id) === String(id)) || null;
  }, [users]);

  const userName = useCallback((id) => {
    const u = userOf(id);
    return u ? (u.employee_name || u.name || u.username || u.login || u.email) : "Unassigned";
  }, [userOf]);

  const userEmail = useCallback((id) => (userOf(id)?.email) || null, [userOf]);

  const teamName = useCallback((id) => {
    const t = teams.find((x) => String(x.id) === String(id));
    return t ? t.name : null;
  }, [teams]);

  const wonStageId = useMemo(() => stages.find((s) => s.is_won)?.id, [stages]);
  const lostStageId = useMemo(() => stages.find((s) => s.is_lost)?.id, [stages]);

  const kpi = useMemo(() => {
    const won = leads.filter((l) => stageFor(l)?.is_won);
    const lost = leads.filter((l) => stageFor(l)?.is_lost);
    const open = leads.filter((l) => { const s = stageFor(l); return !(s?.is_won || s?.is_lost); });
    const wonRevenue = won.reduce((s, l) => s + (Number(l.won_revenue) || 0), 0);
    const pipelineValue = open.reduce((s, l) => s + (Number(l.expected_revenue) || 0) * (Number(l.probability) || 0) / 100, 0);
    const closed = won.length + lost.length;
    return {
      total: leads.length,
      opportunities: leads.filter((l) => l.type === "opportunity").length,
      wonCount: won.length,
      lostCount: lost.length,
      wonRevenue,
      pipelineValue,
      winRate: closed ? Math.round((won.length / closed) * 100) : 0,
    };
  }, [leads, stageFor]);

  const hasActiveFilters = useMemo(
    () => search.trim() !== "" || typeFilter !== "all" || stageFilter !== "all" || teamFilter !== "all" || ownerFilter !== "all" || priorityFilter !== "all" || includeInactive,
    [search, typeFilter, stageFilter, teamFilter, ownerFilter, priorityFilter, includeInactive]
  );

  const resetFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setStageFilter("all");
    setTeamFilter("all");
    setOwnerFilter("all");
    setPriorityFilter("all");
    setIncludeInactive(false);
  };

  const handleReactivateAndRelance = async (l) => {
    try {
      const wasInactive = l.is_active === false || l.active === false;
      if (wasInactive) {
        await api.crm.update(l.id, { is_active: true });
        toast("Client réactivé", "success");
      }
      await openSingleWaRelance({ ...l, is_active: true, active: true });
      await refresh();
    } catch (e) {
      toast(e.message || "Réactivation échouée", "error");
    }
  };

  const toggleTypeFilter = (t) => setTypeFilter((cur) => (cur === t ? "all" : t));
  const toggleStageFilter = (id) => setStageFilter((cur) => (cur !== "all" && String(cur) === String(id) ? "all" : id));

  const openDetail = async (l) => {
    setDetail(l);
    setDetailLoading(true);
    setDetailTab("overview");
    setDetailProducts([]);
    try {
      const d = await api.crm.get(l.id);
      setDetail(d || l);
      const prods = await api.crm.leadProducts.list(l.id, { limit: 100 }).catch(() => []);
      const n = norm(prods);
      setDetailProducts(Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : []);
    } catch {
      setDetail(l);
    }
    setDetailLoading(false);
  };

  const reloadDetail = async () => {
    if (!detail) return;
    try {
      const d = await api.crm.get(detail.id);
      setDetail(d || detail);
      const prods = await api.crm.leadProducts.list(detail.id, { limit: 100 }).catch(() => []);
      const n = norm(prods);
      setDetailProducts(Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : []);
    } catch {}
  };

  const loadHistory = async () => {
    if (!detail) return;
    try {
      const res = await api.crm.history(detail.id);
      setHistory(res?.data || res || []);
    } catch {
      setHistory([]);
    }
  };

  const handleAddActivity = async () => {
    if (!detail) return;
    if (!newActivity.activity_type_id || !newActivity.summary.trim()) {
      toast("Activity type and summary are required", "error");
      return;
    }
    setActionBusy(true);
    try {
      await api.crm.activities.create({
        lead_id: detail.id,
        activity_type_id: parseInt(newActivity.activity_type_id),
        user_id: newActivity.user_id ? parseInt(newActivity.user_id) : undefined,
        date_deadline: newActivity.date_deadline || undefined,
        summary: newActivity.summary.trim(),
      });
      toast("Activity scheduled", "success");
      setNewActivity({ activity_type_id: "", user_id: "", date_deadline: "", summary: "" });
      await reloadDetail();
      await refresh();
    } catch (e) {
      toast(e.message || "Activity creation failed", "error");
    }
    setActionBusy(false);
  };

  const handleDoneActivity = async (a) => {
    try {
      await api.crm.activities.done(a.id);
      toast("Activity completed", "success");
      await reloadDetail();
      await refresh();
    } catch (e) {
      toast(e.message || "Done failed", "error");
    }
  };

  const handleSnoozeActivity = async (a) => {
    const input = window.prompt(`Snooze (days, default ${a.type_delay ?? 3}):`, "3");
    if (input === null) return;
    try {
      await api.crm.activities.snooze(a.id, { days: parseInt(input) || undefined });
      toast("Activity rescheduled", "success");
      await reloadDetail();
      await refresh();
    } catch (e) {
      toast(e.message || "Snooze failed", "error");
    }
  };

  const handleAddMessage = async () => {
    if (!detail) return;
    if (!newMessage.body.trim()) { toast("Message body is required", "error"); return; }
    setActionBusy(true);
    try {
      await api.crm.addMessage(detail.id, { body: newMessage.body.trim(), type: newMessage.type });
      toast("Message added", "success");
      setNewMessage({ body: "", type: "note" });
      await reloadDetail();
    } catch (e) {
      toast(e.message || "Add message failed", "error");
    }
    setActionBusy(false);
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !detail) return;
    setUploading(true);
    try {
      const res = await api.upload(file);
      const file_path = res?.file_path;
      if (!file_path) throw new Error("Upload did not return a file_path");
      await api.crm.addAttachment(detail.id, { name: file.name, file_name: file.name, file_path });
      toast("Attachment added", "success");
      await reloadDetail();
    } catch (err) {
      toast(err.message || "Upload failed", "error");
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleDeleteAttachment = async (att) => {
    if (!detail) return;
    if (!window.confirm(`Delete attachment "${att.name || att.file_name}"?`)) return;
    try {
      await api.crm.removeAttachment(detail.id, att.id);
      toast("Attachment deleted", "success");
      await reloadDetail();
    } catch (e) {
      toast(e.message || "Delete failed", "error");
    }
  };

  const handleDeleteMessage = async (m) => {
    if (!detail) return;
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.crm.removeMessage(detail.id, m.id);
      toast("Message deleted", "success");
      await reloadDetail();
    } catch (e) {
      toast(e.message || "Delete failed", "error");
    }
  };

  const refresh = async () => {
    await loadLeads();
  };

  const openNew = () => {
    setEditing(null);
    const today = new Date().toISOString().slice(0, 10);
    setForm({
      name: "", type: "lead", stage_id: "", company: "",
      email_from: "", phone: "", mobile: "",
      user_id: "", team_id: "", priority: 1,
      expected_revenue: "", plan_revenue: "", probability: "",
      source_id: "", medium_id: "", campaign_id: "",
      date_open: today, date_deadline: "", description: "", tag_ids: [],
    });
    setShowNew(true);
  };

  const openEdit = (l) => {
    setEditing(l);
    setForm({
      name: l.name || "", type: l.type || "lead", stage_id: l.stage_id ?? "",
      company: l.company || "",
      email_from: l.email_from || "", phone: l.phone || "", mobile: l.mobile || "",
      user_id: l.user_id ?? "", team_id: l.team_id ?? "", priority: l.priority ?? 1,
      expected_revenue: l.expected_revenue ?? "", plan_revenue: l.plan_revenue ?? "",
      probability: l.probability ?? "", source_id: l.source_id ?? "", medium_id: l.medium_id ?? "",
      campaign_id: l.campaign_id ?? "", date_open: l.date_open ? String(l.date_open).slice(0, 10) : "",
      date_deadline: l.date_deadline ? String(l.date_deadline).slice(0, 10) : "",
      description: l.description || "", tag_ids: l.tag_ids || [],
    });
    setShowNew(true);
  };

  const toggleTag = (id) => {
    setForm((f) => {
      const tagArr = f.tag_ids || [];
      return {
        ...f,
        tag_ids: tagArr.includes(id) ? tagArr.filter((t) => t !== id) : [id, ...tagArr],
      };
    });
  };

  const buildPayload = (f) => ({
    name: f.name.trim() || undefined,
    type: f.type || undefined,
    stage_id: f.stage_id ? parseInt(f.stage_id) : undefined,
    company: f.company.trim() || undefined,
    email_from: f.email_from.trim() || undefined,
    phone: f.phone.trim() || undefined,
    mobile: f.mobile.trim() || undefined,
    user_id: f.user_id ? parseInt(f.user_id) : undefined,
    team_id: f.team_id ? parseInt(f.team_id) : undefined,
    priority: f.priority !== "" ? parseInt(f.priority) : undefined,
    expected_revenue: f.expected_revenue !== "" ? parseFloat(f.expected_revenue) : undefined,
    plan_revenue: f.plan_revenue !== "" ? parseFloat(f.plan_revenue) : undefined,
    probability: f.probability !== "" ? parseFloat(f.probability) : undefined,
    source_id: f.source_id ? parseInt(f.source_id) : undefined,
    medium_id: f.medium_id ? parseInt(f.medium_id) : undefined,
    campaign_id: f.campaign_id ? parseInt(f.campaign_id) : undefined,
    date_open: f.date_open || undefined,
    date_deadline: f.date_deadline || undefined,
    description: f.description.trim() || undefined,
    tag_ids: f.tag_ids || [],
  });

  const handleCreate = async () => {
    if (!form.name.trim()) { toast("Lead name is required", "error"); return; }
    setCreating(true);
    try {
      await api.crm.create(buildPayload(form));
      toast("Lead created", "success");
      setShowNew(false);
      setEditing(null);
      await refresh();
    } catch (e) {
      toast(e.message || "Create failed", "error");
    }
    setCreating(false);
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!form.name.trim()) { toast("Lead name is required", "error"); return; }
    setCreating(true);
    try {
      await api.crm.update(editing.id, buildPayload(form));
      toast("Lead updated", "success");
      setShowNew(false);
      setEditing(null);
      await refresh();
    } catch (e) {
      toast(e.message || "Update failed", "error");
    }
    setCreating(false);
  };

  const handleDelete = async (l) => {
    if (!window.confirm(`Delete lead "${l.name || l.id}"?`)) return;
    try {
      await api.crm.remove(l.id);
      toast("Lead deleted", "success");
      setDetail(null);
      await refresh();
    } catch (e) {
      toast(e.message || "Delete failed", "error");
    }
  };

  const handleReopen = async (l) => {
    const firstOpen = stages.find((s) => !s.is_won && !s.is_lost);
    if (!firstOpen) { toast("No open stage available", "error"); return; }
    try {
      await api.crm.update(l.id, { stage_id: firstOpen.id });
      toast("Lead reopened", "success");
      await refresh();
    } catch (e) {
      toast(e.message || "Reopen failed", "error");
    }
  };

  // ---- Actions métier ----
  const runAction = async (action, lead, body) => {
    setActionBusy(true);
    try {
      let res;
      if (action === "convert") res = await api.crm.convert(lead.id);
      else if (action === "assign") res = await api.crm.assign(lead.id, body);
      else if (action === "won") res = await api.crm.won(lead.id, body);
      else if (action === "lost") res = await api.crm.lost(lead.id, body);
      else if (action === "quotation") {
        res = await api.crm.createQuotation(lead.id, body);
        const created = res?.data || res;
        setCreatedQuotation(created);
        toast(`Devis ${created?.name || ""} créé — vous pouvez l'envoyer directement`, "success");
        if (res?.warning) toast(res.warning, "info");
        await refresh();
        if (detail) await reloadDetail();
        setActionBusy(false);
        return;
      }
      toast(res?.message || `${action} done`, "success");
      if (res?.warning) toast(res.warning, "info");
      setActionTarget(null);
      setQuotPreview(null);
      setCreatedQuotation(null);
      if (detail) setDetail(null);
      await refresh();
    } catch (e) {
      toast(e.message || `${action} failed`, "error");
    }
    setActionBusy(false);
  };

  const handleQuotPreview = async (lead) => {
    if (!lead) return;
    setQuotPreviewLoading(true);
    try {
      const res = await api.crm.quotationPreview(lead.id);
      setQuotPreview(res?.data || res || null);
    } catch (e) {
      toast(e.message || "Preview failed", "error");
      setQuotPreview(null);
    }
    setQuotPreviewLoading(false);
  };

  const handleSendCreatedQuotation = async (channel) => {
    if (!createdQuotation?.id) { toast("Aucun devis à envoyer", "error"); return; }
    setQuotSendBusy(true);
    try {
      if (channel === "whatsapp") {
        await api.sales.sendWhatsApp(createdQuotation.id, {});
        toast("Devis envoyé par WhatsApp (file d'attente)", "success");
      } else {
        await api.sales.sendEmail(createdQuotation.id, {});
        toast("Devis envoyé par Email (file d'attente)", "success");
      }
    } catch (e) {
      toast(e.message || `Envoi ${channel} échoué`, "error");
    }
    setQuotSendBusy(false);
  };

  const openSingleWaRelance = async (lead) => {
    setSelectedIds([lead.id]);
    setWaBulkTemplateId("");
    setWaBulkTemplates([]);
    setWaBulkOpen(true);
    try {
      const res = await api.mail.templates.list({ limit: 100 });
      const n = norm(res);
      const rows = Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : [];
      const active = rows.filter((t) => t.is_active !== false && t.channel_type === "whatsapp");
      setWaBulkTemplates(active);
      if (active.length === 1) setWaBulkTemplateId(String(active[0].id));
    } catch (e) {
      toast(e.message || "Failed to load templates", "error");
    }
  };

  const handleDrop = async (col) => {
    const id = dragging;
    setDragging(null);
    setDragOver(null);
    if (!id) return;
    const lead = leads.find((l) => l.id === id);
    if (!lead) return;
    if (col.is_won || col.is_lost) {
      toast("Terminal stage — use the Won/Lost actions", "error");
      return;
    }
    if (String(lead.stage_id) === String(col.id)) return;
    const previousStageId = lead.stage_id;
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, stage_id: col.id } : l)));
    try {
      await api.crm.update(lead.id, { stage_id: col.id });
      toast(`Moved to "${col.name}"`, "success");
    } catch (e) {
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, stage_id: previousStageId } : l)));
      toast(e.message || "Move failed", "error");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const openMerge = () => {
    setMergeTarget({ principal: selectedIds[0], others: selectedIds.slice(1) });
  };

  const handleMerge = async () => {
    if (!mergeTarget?.principal || mergeTarget.others.length === 0) { toast("Select a principal and at least one duplicate", "error"); return; }
    setActionBusy(true);
    try {
      const res = await api.crm.merge(mergeTarget.principal, { lead_ids: mergeTarget.others });
      toast(`Merged ${res?.merged_lead_ids?.length || mergeTarget.others.length} duplicate(s)`, "success");
      setMergeTarget(null);
      setSelectedIds([]);
      await refresh();
    } catch (e) {
      toast(e.message || "Merge failed", "error");
    }
    setActionBusy(false);
  };

  const openBulkMail = async () => {
    setMailTemplateId("");
    setMailTemplates([]);
    setMailOpen(true);
    try {
      const res = await api.mail.templates.list({ limit: 100 });
      const n = norm(res);
      const rows = Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : [];
      const active = rows.filter((t) => t.is_active !== false && (t.channel_type === "email" || !t.channel_type));
      setMailTemplates(active);
      if (active.length === 1) setMailTemplateId(String(active[0].id));
    } catch (e) {
      toast(e.message || "Failed to load templates", "error");
    }
  };

  const openBulkWhatsApp = async () => {
    setWaBulkTemplateId("");
    setWaBulkTemplates([]);
    setWaBulkOpen(true);
    try {
      const res = await api.mail.templates.list({ limit: 100 });
      const n = norm(res);
      const rows = Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : [];
      const active = rows.filter((t) => t.is_active !== false && t.channel_type === "whatsapp");
      setWaBulkTemplates(active);
      if (active.length === 1) setWaBulkTemplateId(String(active[0].id));
    } catch (e) {
      toast(e.message || "Failed to load templates", "error");
    }
  };

  const handleBulkMail = async () => {
    if (!mailTemplateId) { toast("Select a template", "error"); return; }
    if (selectedIds.length > 250) { toast("Maximum 250 leads per send", "error"); return; }
    setMailBusy(true);
    try {
      const tpl = mailTemplates.find((t) => String(t.id) === String(mailTemplateId));
      const res = await api.mail.bulk({ template_id: parseInt(mailTemplateId, 10), lead_ids: selectedIds, attach_documents: !!(tpl && tpl.attach_documents) });
      const queued = Number(res?.queued) || 0;
      const skippedCount = Array.isArray(res?.skipped) ? res.skipped.length : 0;
      if (queued === 0) toast("No email scheduled — check that selected leads have an email address", skippedCount ? "info" : "error");
      else toast(`${queued} email${queued === 1 ? "" : "s"} scheduled${skippedCount ? `, ${skippedCount} lead${skippedCount === 1 ? "" : "s"} skipped (no email)` : ""}${tpl?.attach_documents ? " (with lead attachments)" : ""}`, "success");
      setMailOpen(false);
      setSelectedIds([]);
    } catch (e) {
      toast(e.message || "Bulk send failed", "error");
    }
    setMailBusy(false);
  };

  const handleBulkWhatsApp = async () => {
    if (!waBulkTemplateId) { toast("Select a template", "error"); return; }
    if (selectedIds.length > 250) { toast("Maximum 250 leads per send", "error"); return; }
    setWaBulkBusy(true);
    try {
      const res = await api.mail.bulk({ template_id: parseInt(waBulkTemplateId, 10), lead_ids: selectedIds, channel: "whatsapp" });
      const queued = Number(res?.queued) || 0;
      const skippedCount = Array.isArray(res?.skipped) ? res.skipped.length : 0;
      if (queued === 0) toast("No WhatsApp scheduled — check that selected leads have a phone number", skippedCount ? "info" : "error");
      else toast(`${queued} WhatsApp${queued === 1 ? "" : "s"} scheduled${skippedCount ? `, ${skippedCount} lead${skippedCount === 1 ? "" : "s"} skipped (no phone)` : ""}`, "success");
      setWaBulkOpen(false);
      setSelectedIds([]);
    } catch (e) {
      toast(e.message || "Bulk WhatsApp failed", "error");
    }
    setWaBulkBusy(false);
  };

  const handleExport = async () => {
    try {
      await api.crm.export(filters);
      toast("Export downloaded", "success");
    } catch (e) {
      toast(e.message || "Export failed", "error");
    }
  };

  useEffect(() => {
    if (actionTarget?.action === "quotation" && actionTarget?.lead && !quotPreview && !quotPreviewLoading && !createdQuotation) {
      handleQuotPreview(actionTarget.lead);
    }
    if (!actionTarget) {
      setQuotPreview(null);
      setCreatedQuotation(null);
    }
  }, [actionTarget, quotPreview, quotPreviewLoading, createdQuotation]);

  const onSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const visibleLeads = useMemo(() => {
    const sorted = leads.slice().sort((a, b) => {
      let va = a[sortBy];
      let vb = b[sortBy];
      if (sortBy === "expected_revenue" || sortBy === "probability" || sortBy === "priority") {
        va = Number(va) || 0;
        vb = Number(vb) || 0;
      } else if (sortBy === "date_open" || sortBy === "date_deadline" || sortBy === "date_closed") {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      } else {
        va = String(va || "").toLowerCase();
        vb = String(vb || "").toLowerCase();
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    const start = (page - 1) * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [leads, sortBy, sortDir, page]);

  const leadDateKey = (l) => {
    if (l.date_deadline) return String(l.date_deadline).slice(0, 10);
    if (l.next_activity?.date_deadline) return String(l.next_activity.date_deadline).slice(0, 10);
    return null;
  };

  const calByDay = useMemo(() => {
    const map = {};
    for (const l of leads) {
      const k = leadDateKey(l);
      if (!k) continue;
      (map[k] || (map[k] = [])).push(l);
    }
    return map;
  }, [leads]);

  const calState = useMemo(() => {
    const year = calDate.getFullYear();
    const month = calDate.getMonth();
    const pad2 = (n) => String(n).padStart(2, "0");
    const leading = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < leading; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
    const keyOf = (cell) => `${cell.getFullYear()}-${pad2(cell.getMonth() + 1)}-${pad2(cell.getDate())}`;
    const monthLabel = calDate.toLocaleString("en-US", { month: "long", year: "numeric" });
    return { weeks, todayKey, keyOf, monthLabel };
  }, [calDate]);

  const chipFor = (l) => {
    const s = stageFor(l);
    if (s?.is_won) return { bg: "#f0f7f0", color: "#225f2b" };
    if (s?.is_lost) return { bg: "#fff1f2", color: "#be123c" };
    return { bg: "#eef6f3", color: "#0f766e" };
  };

  const hasMonthLeads = useMemo(() => {
    for (const week of calState.weeks) {
      for (const c of week) {
        if (c && (calByDay[calState.keyOf(c)] || []).length > 0) return true;
      }
    }
    return false;
  }, [calState, calByDay]);

  /* PIVOT + GRAPH HELPERS — temporarily disabled (CRM Dashboard already covers these).
  const dimValue = useCallback((l, dim) => {
    switch (dim) {
      case "stage": return stageFor(l)?.name || "No stage";
      case "type": return l.type === "opportunity" ? "Opportunity" : "Lead";
      case "team": return teamName(l.team_id) || "No team";
      case "owner": return userName(l.user_id);
      case "source": return l.source_name || "No source";
      case "month": return l.date_open ? String(l.date_open).slice(0, 7) : (l.date_deadline ? String(l.date_deadline).slice(0, 7) : "No date");
      default: return "All";
    }
  }, [stageFor, teamName, userName]);

  const measureVal = (a, measure) => {
    switch (measure) {
      case "exp": return a.exp;
      case "won": return a.won;
      case "wonCount": return a.wonCount;
      default: return a.count;
    }
  };

  const fmtVal = (v, measure) => (measure === "count" || measure === "wonCount") ? String(v) : fmt.money(v);

  const pivot = useMemo(() => {
    const rowsMap = new Map();
    const colSet = new Set();
    for (const l of leads) {
      const rv = dimValue(l, pivDim);
      const cv = pivCol === "none" ? "Total" : dimValue(l, pivCol);
      colSet.add(cv);
      let r = rowsMap.get(rv);
      if (!r) { r = new Map(); rowsMap.set(rv, r); }
      const a = r.get(cv) || { count: 0, exp: 0, won: 0, wonCount: 0 };
      a.count += 1;
      a.exp += Number(l.expected_revenue) || 0;
      a.won += Number(l.won_revenue) || 0;
      if (stageFor(l)?.is_won) a.wonCount += 1;
      r.set(cv, a);
    }
    const cols = pivCol === "none" ? ["Total"] : [...colSet];
    cols.sort((a, b) => a.localeCompare(b));
    const rows = [...rowsMap.entries()].map(([key, r]) => {
      const cells = cols.map((c) => measureVal(r.get(c) || { count: 0, exp: 0, won: 0, wonCount: 0 }, pivMeasure));
      return { key, cells, total: cells.reduce((s, v) => s + v, 0) };
    });
    rows.sort((a, b) => a.key.localeCompare(b.key));
    const colTotals = cols.map((_, i) => rows.reduce((s, r) => s + r.cells[i], 0));
    const grandTotal = colTotals.reduce((s, v) => s + v, 0);
    return { rows, cols, colTotals, grandTotal };
  }, [leads, pivDim, pivCol, pivMeasure, stageFor, dimValue]);

  const canRowFilter = (dim) => dim === "stage" || dim === "team" || dim === "owner" || dim === "type";

  const applyRowFilter = (key) => {
    if (pivDim === "stage") {
      const s = stages.find((x) => x.name === key);
      if (s) toggleStageFilter(s.id);
    } else if (pivDim === "team") {
      const t = teams.find((x) => x.name === key);
      if (t) setTeamFilter(t.id);
    } else if (pivDim === "owner") {
      const u = users.find((x) => userName(x.id) === key);
      if (u) setOwnerFilter(u.id);
    } else if (pivDim === "type") {
      toggleTypeFilter(key === "Lead" ? "lead" : "opportunity");
    }
  };

  const graphData = useMemo(() => {
    const map = new Map();
    for (const l of leads) {
      const gv = dimValue(l, graphGroup);
      const a = map.get(gv) || { count: 0, exp: 0, won: 0, wonCount: 0 };
      a.count += 1;
      a.exp += Number(l.expected_revenue) || 0;
      a.won += Number(l.won_revenue) || 0;
      if (stageFor(l)?.is_won) a.wonCount += 1;
      map.set(gv, a);
    }
    return [...map.entries()].map(([k, v]) => ({ key: k, ...v })).sort((a, b) => a.key.localeCompare(b.key));
  }, [leads, graphGroup, stageFor, dimValue]);

  const gVal = (d) => measureVal(d, graphMeasure);
  const gTotal = graphData.reduce((s, d) => s + gVal(d), 0);
  const gMax = Math.max(1, ...graphData.map(gVal));
  let _acc = 0;
  const donutSegments = graphData.map((d, i) => {
    const v = gVal(d);
    const len = gTotal ? Math.round((v / gTotal) * 100) : 0;
    const seg = { ...d, val: v, pct: len, len, offset: _acc, color: CHART_COLORS[i % CHART_COLORS.length] };
    _acc += len;
    return seg;
  });
  const LINE_W = 600, LINE_H = 220, LINE_PAD_L = 46, LINE_PAD_R = 12, LINE_PAD_T = 12, LINE_PAD_B = 28;
  const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const lx = (i) => LINE_PAD_L + (graphData.length <= 1 ? (LINE_W - LINE_PAD_L - LINE_PAD_R) / 2 : (i / (graphData.length - 1)) * (LINE_W - LINE_PAD_L - LINE_PAD_R));
  const ly = (v) => LINE_PAD_T + (LINE_H - LINE_PAD_T - LINE_PAD_B) - (v / (gMax * 1.15)) * (LINE_H - LINE_PAD_T - LINE_PAD_B);
  const linePointsStr = graphData.map((d, i) => `${lx(i)},${ly(gVal(d))}`).join(" ");
  const shortMonth = (k) => {
    if (k === "No date") return k;
    const [yy, mm] = k.split("-");
    return `${MONTHS_SHORT[Number(mm) - 1] || k} ${yy.slice(2)}`;
  };
*/

  const actionLead = actionTarget?.lead;
  const isLeadType = actionTarget?.lead?.type === "lead";
  const isTerminal = (l) => {
    const s = stageFor(l);
    return !!(s?.is_won || s?.is_lost);
  };

  const renderCard = (l) => {
    const tb = typeBadge(l.type);
    const pr = Number(l.probability) || 0;
    const prColor = PRIORITY_COLORS[l.priority] || PRIORITY_COLORS[1];
    const st = stageFor(l);
    const isTerm = !!(st?.is_won || st?.is_lost);
    return (
      <div
        key={l.id}
        draggable={canManage}
        onDragStart={() => setDragging(l.id)}
        onDragEnd={() => setDragging(null)}
        style={{
          background: "#fff", border: dragging === l.id ? "2px solid #225f2b" : "1px solid #e2e7e2",
          borderRadius: 8, padding: 12, marginBottom: 8, cursor: canManage ? "grab" : "default",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        <div onClick={() => openDetail(l)} style={{ cursor: "pointer" }}>
          <strong style={{ fontSize: 13.5, color: "#225f2b", lineHeight: 1.35, display: "block" }}>{l.name || "—"}</strong>
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
          {isTerm && (
            <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700, background: st.is_won ? "#f0f7f0" : "#fff1f2", color: st.is_won ? "#225f2b" : "#be123c", border: st.is_won ? "1px solid #bbe3c4" : "1px solid #fecdd3" }}>
              {st.is_won ? "✓ WON" : "✗ LOST"}
            </span>
          )}
          {(l.is_active === false || l.active === false) && (
            <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700, background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" }}>ARCHIVÉ</span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <span style={{ background: tb.bg, color: tb.text, padding: "2px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700 }}>{tb.label}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{fmt.money(l.expected_revenue)}</span>
          <span style={{ marginLeft: "auto", fontSize: 11, color: prColor, fontWeight: 600 }}>● {PRIORITY_LABELS[l.priority] || "—"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          <div style={{ flex: 1, height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: `${Math.min(100, pr)}%`, height: "100%", background: "#225f2b" }} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#334155" }}>{pr}%</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, minWidth: 0 }}>
          {l.user_id && <EmployeeAvatar employee={{ name: userName(l.user_id), lewoo_uid: userOf(l.user_id)?.lewoo_uid }} size={24} fontSize={9} />}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: "#1e293b", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {userName(l.user_id)}{l.team_id ? ` · ${teamName(l.team_id) || ""}` : ""}
            </div>
            {userEmail(l.user_id) && <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{userEmail(l.user_id)}</div>}
          </div>
        </div>
        {l.next_activity && (
          <div style={{ color: "#b45309", display: "flex", alignItems: "center", gap: 4, marginTop: 6, fontSize: 11 }}>
            <CalendarClock size={11} /> {fmt.date(l.next_activity.date_deadline)} — {l.next_activity.summary || l.next_activity.type_name}
          </div>
        )}
        {l.tags?.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
            {l.tags.slice(0, 4).map((t) => (
              <span key={t} style={{ background: "#eef6f3", color: "#0f766e", padding: "1px 7px", borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{t}</span>
            ))}
          </div>
        )}
        {canManage && !isTerminal(l) && (
          <div style={{ display: "flex", gap: 4, marginTop: 9, borderTop: "1px solid #f1f3f0", paddingTop: 8, flexWrap: "wrap" }}>
            {isLeadType ? (
              <button title="Convert to opportunity" onClick={() => setActionTarget({ action: "convert", lead: l })} style={cardBtn("#225f2b")}>
                <Split size={12} /> Convert
              </button>
            ) : (
              <>
                <button title="Mark Won" onClick={() => setActionTarget({ action: "won", lead: l })} style={cardBtn("#225f2b")}>
                  <Check size={12} /> Won
                </button>
                <button title="Mark Lost" onClick={() => setActionTarget({ action: "lost", lead: l })} style={cardBtn("#be123c")}>
                  <X size={12} /> Lost
                </button>
              </>
            )}
            <button title="Assign" onClick={() => setActionTarget({ action: "assign", lead: l })} style={cardBtn("#475569")}>
              <UserPlus size={12} /> Assign
            </button>
            {(l.phone || l.mobile) && (
              <button title="Relancer via WhatsApp" onClick={() => openSingleWaRelance(l)} style={cardBtn("#166534")}>
                <MessageCircle size={12} /> Relancer
              </button>
            )}
            {l.type === "opportunity" && (
              <button title="Devis + envoi" onClick={() => { setQuotPreview(null); setCreatedQuotation(null); setActionTarget({ action: "quotation", lead: l }); }} style={cardBtn("#0f766e")}>
                <FileText size={12} /> Devis
              </button>
            )}
          </div>
        )}
        {canManage && isTerminal(l) && stageFor(l)?.is_lost && (
          <div style={{ display: "flex", gap: 4, marginTop: 9, borderTop: "1px solid #f1f3f0", paddingTop: 8, flexWrap: "wrap" }}>
            <button title="Reopen lead" onClick={() => handleReopen(l)} style={cardBtn("#475569")}>
              <RotateCcw size={12} /> Reopen
            </button>
          </div>
        )}
        {(l.is_active === false || l.active === false) && (
          <div style={{ display: "flex", gap: 4, marginTop: 9, borderTop: "1px solid #fef3c7", paddingTop: 8, flexWrap: "wrap", background: "#fffbeb", borderRadius: 6, padding: "8px" }}>
            <button title="Réactiver et relancer par WhatsApp" onClick={() => handleReactivateAndRelance(l)} style={cardBtn("#92400e")}>
              <RotateCcw size={12} /> Réactiver & Relancer
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!loaded) return <LoadingScreen />;

  return (
<div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="CRM"
        subtitle="Sales pipeline — leads and opportunities"
        actions={
          <>
            {selectedIds.length > 1 && (
              <button onClick={openMerge} className="ess-btn ess-btn-secondary">
                <GitMerge size={16} /> Merge ({selectedIds.length})
              </button>
            )}
            {selectedIds.length > 0 && canManage && (
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={openBulkMail} className="ess-btn ess-btn-secondary">
                  <Mail size={16} /> Email ({selectedIds.length})
                </button>
                <button onClick={openBulkWhatsApp} className="ess-btn ess-btn-secondary" style={{ background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" }}>
                  <MessageCircle size={16} /> WhatsApp ({selectedIds.length})
                </button>
              </div>
            )}
            <button onClick={() => setShowStats((s) => !s)} className="ess-btn ess-btn-secondary" title={showStats ? "Collapse stats to see the list" : "Show KPI stats"}>
              <LayoutGrid size={16} /> {showStats ? "Hide stats" : "Show stats"}
            </button>
            <button onClick={handleExport} className="ess-btn ess-btn-secondary">
              <Download size={16} /> Export
            </button>
            <div style={{ display: "flex", border: "1px solid #cbd5e1", borderRadius: 6, overflow: "hidden" }}>
              <button
                onClick={() => setView("list")}
                style={{ ...viewBtn, background: view === "list" ? "#225f2b" : "#fff", color: view === "list" ? "#fff" : "#475569" }}
              >
                <List size={15} /> List
              </button>
              <button
                onClick={() => setView("kanban")}
                style={{ ...viewBtn, background: view === "kanban" ? "#225f2b" : "#fff", color: view === "kanban" ? "#fff" : "#475569", borderLeft: "1px solid #cbd5e1" }}
              >
                <LayoutGrid size={15} /> Kanban
              </button>
              <button
                onClick={() => setView("calendar")}
                style={{ ...viewBtn, background: view === "calendar" ? "#225f2b" : "#fff", color: view === "calendar" ? "#fff" : "#475569", borderLeft: "1px solid #cbd5e1" }}
              >
                <CalendarDays size={15} /> Calendar
              </button>
              {/* PIVOT + GRAPH BUTTONS — temporarily disabled (CRM Dashboard already covers these).
              <button
                onClick={() => setView("pivot")}
                style={{ ...viewBtn, background: view === "pivot" ? "#225f2b" : "#fff", color: view === "pivot" ? "#fff" : "#475569", borderLeft: "1px solid #cbd5e1" }}
              >
                <Table2 size={15} /> Pivot
              </button>
              <button
                onClick={() => setView("graph")}
                style={{ ...viewBtn, background: view === "graph" ? "#225f2b" : "#fff", color: view === "graph" ? "#fff" : "#475569", borderLeft: "1px solid #cbd5e1" }}
              >
                <BarChart3 size={15} /> Graph
              </button>
              */}
            </div>
            {canManage && (
              <button onClick={openNew} className="ess-btn ess-btn-primary">
                <Plus size={16} /> New Lead
              </button>
            )}
          </>
        }
      />

      {showStats && (<>
      <div className="ess-sg ess-sg4" style={{ marginBottom: "12px" }}>
        <KpiCard
          label="Total Leads"
          value={String(kpi.total)}
          icon={Target}
          trend={hasActiveFilters ? "Click to reset filters" : "All pipeline"}
          active={!hasActiveFilters}
          onClick={resetFilters}
        />
        <KpiCard
          label="Opportunities"
          value={String(kpi.opportunities)}
          icon={TrendingUp}
          trend="Converted deals"
          active={typeFilter === "opportunity"}
          onClick={() => toggleTypeFilter("opportunity")}
        />
        <KpiCard
          label="Pipeline Value"
          value={fmt.moneyShort(kpi.pipelineValue)}
          icon={Banknote}
          trend={fmt.money(kpi.pipelineValue)}
        />
        <KpiCard
          label="Win Rate"
          value={`${kpi.winRate}%`}
          icon={Gauge}
          trend={`${kpi.wonCount} won / ${kpi.lostCount} lost`}
        />
      </div>
      <div className="ess-sg ess-sg4" style={{ marginBottom: "12px" }}>
        <KpiCard
          label="Won"
          value={String(kpi.wonCount)}
          icon={Check}
          trend={wonStageId ? "Click to view won deals" : "No won stage"}
          active={wonStageId !== undefined && stageFilter !== "all" && String(stageFilter) === String(wonStageId)}
          onClick={wonStageId ? () => toggleStageFilter(wonStageId) : undefined}
        />
        <KpiCard
          label="Lost"
          value={String(kpi.lostCount)}
          icon={X}
          trend={lostStageId ? "Click to view lost deals" : "No lost stage"}
          active={lostStageId !== undefined && stageFilter !== "all" && String(stageFilter) === String(lostStageId)}
          onClick={lostStageId ? () => toggleStageFilter(lostStageId) : undefined}
        />
        <KpiCard
          label="Won Revenue"
          value={kpi.wonRevenue ? fmt.moneyShort(kpi.wonRevenue) : "—"}
          icon={Banknote}
          trend={kpi.wonRevenue ? fmt.money(kpi.wonRevenue) : "Amount not recorded"}
        />
        <KpiCard
          label="Avg Cycle"
          value={dash?.avg_cycle_days != null ? `${dash.avg_cycle_days}d` : "—"}
          icon={CalendarClock}
          trend="Days to close"
        />
      </div>
      </>)}

      {/* Filtres */}
      <div style={{ background: "white", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e7e2", marginBottom: "16px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
          <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, phone, company…"
              style={{ ...inputStyle, padding: "7px 12px 7px 32px" }}
            />
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ ...inputStyle, cursor: "pointer", flex: "1 1 130px", padding: "7px 10px" }}>
            <option value="all">All Types</option>
            <option value="lead">Lead</option>
            <option value="opportunity">Opportunity</option>
          </select>
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} style={{ ...inputStyle, cursor: "pointer", flex: "1 1 130px", padding: "7px 10px" }}>
            <option value="all">All Stages</option>
            {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} style={{ ...inputStyle, cursor: "pointer", flex: "1 1 130px", padding: "7px 10px" }}>
            <option value="all">All Teams</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} style={{ ...inputStyle, cursor: "pointer", flex: "1 1 130px", padding: "7px 10px" }}>
            <option value="all">All Owners</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.username || u.email || u.login}</option>)}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} style={{ ...inputStyle, cursor: "pointer", flex: "1 1 120px", padding: "7px 10px" }}>
            <option value="all">All Priorities</option>
            {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569", cursor: "pointer", background: includeInactive ? "#fffbeb" : "#fff", border: `1px solid ${includeInactive ? "#f59e0b" : "#cbd5e1"}`, borderRadius: 6, padding: "7px 10px", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} style={{ width: 14, height: 14 }} />
            Inclure inactifs
          </label>
        </div>
        {includeInactive && <div style={{ marginTop: 8, fontSize: 11, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "6px 10px" }}>Mode relance : {total} leads (actifs + archivés). Coche plusieurs lignes → <strong>WhatsApp ({selectedIds.length})</strong> pour envoi groupé (max 250 `src/services/erpApi.js:275`). Les archivés portent le badge <span style={{ background: "#fef3c7", padding: "1px 6px", borderRadius: 4, fontWeight: 700 }}>ARCHIVÉ</span> et peuvent être réactivés.</div>}
      </div>

      {loading ? (
        <LoadingScreen />
      ) : view === "kanban" ? (
        <div style={{ display: "flex", gap: 16, overflowX: "auto", alignItems: "flex-start", paddingBottom: 16 }}>
          {columns.map((col) => {
            const colLeads = leads.filter((l) => String(l.stage_id) === String(col.id));
            const totalAmt = colLeads.reduce((s, l) => s + (Number(l.expected_revenue) || 0), 0);
            const term = col.is_won || col.is_lost;
            return (
              <div
                key={col.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!term) setDragOver(col.id);
                }}
                onDragLeave={() => setDragOver((d) => (d === col.id ? null : d))}
                onDrop={() => handleDrop(col)}
                style={{
                  minWidth: 280, maxWidth: 320, flex: 1, background: dragOver === col.id ? "#f0f7f0" : "#f8faf8",
                  borderRadius: 10, border: dragOver === col.id ? "2px solid #225f2b" : "1px solid #e2e7e2",
                  padding: 12,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div>
                    <strong style={{ fontSize: 13, color: "#1e293b" }}>{col.name}</strong>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{colLeads.length} lead{colLeads.length > 1 ? "s" : ""} · {fmt.moneyShort(totalAmt)}</div>
                  </div>
                  {term && (
                    <span style={{ padding: "2px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700, background: col.is_won ? "#f0f7f0" : "#fff1f2", color: col.is_won ? "#225f2b" : "#be123c" }}>
                      {col.is_won ? "WON" : "LOST"}
                    </span>
                  )}
                </div>
                <div className="no-scrollbar" style={{ maxHeight: "70vh", overflowY: "auto", marginRight: -4, paddingRight: 4 }}>
                  {colLeads.length === 0 ? (
                    <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: "18px 0", border: "1px dashed #e2e7e2", borderRadius: 8 }}>
                      Drop leads here
                    </div>
                  ) : (
                    colLeads.map(renderCard)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : view === "calendar" ? (
        <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid #e2e7e2", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button onClick={() => setCalDate(new Date(calDate.getFullYear(), calDate.getMonth() - 1, 1))} style={iconBtn("#475569")}>‹</button>
              <button onClick={() => setCalDate(new Date(calDate.getFullYear(), calDate.getMonth() + 1, 1))} style={iconBtn("#475569")}>›</button>
              <button onClick={() => setCalDate(new Date())} style={{ ...iconBtn("#225f2b"), fontWeight: 600 }}>Today</button>
            </div>
            <strong style={{ fontSize: 16, color: "#1e293b", marginLeft: 4 }}>{calState.monthLabel}</strong>
            {!hasMonthLeads && <span style={{ fontSize: 12, color: "#94a3b8" }}>— nothing scheduled this month</span>}
            <div style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center", fontSize: 11, color: "#64748b", flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: "#eef6f3", border: "1px solid #99c9b0", display: "inline-block" }} /> Open</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: "#f0f7f0", border: "1px solid #8fbf9a", display: "inline-block" }} /> Won</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: "#fff1f2", border: "1px solid #f3a6b3", display: "inline-block" }} /> Lost</span>
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 760 }}>
              <thead>
                <tr>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                    <th key={d} style={{ padding: "8px 6px", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", borderBottom: "1px solid #e2e7e2", background: "#fafbfa", textAlign: "center" }}>{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calState.weeks.map((week, wi) => (
                  <tr key={wi}>
                    {week.map((cell, ci) => {
                      if (!cell) return <td key={`b${wi}-${ci}`} style={{ border: "1px solid #e2e7e2", background: "#fafbfa" }} />;
                      const k = calState.keyOf(cell);
                      const dayLeads = calByDay[k] || [];
                      const isToday = k === calState.todayKey;
                      return (
                        <td key={ci} style={{ border: "1px solid #e2e7e2", verticalAlign: "top", minHeight: 92, padding: 4, background: isToday ? "#f4faf5" : "#fff" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: isToday ? "#225f2b" : "#475569", marginBottom: 4, paddingLeft: 2 }}>{cell.getDate()}</div>
                          {dayLeads.slice(0, 3).map((l) => {
                            const c = chipFor(l);
                            return (
                              <button key={l.id} onClick={() => openDetail(l)} style={{ display: "block", width: "100%", textAlign: "left", fontSize: 10.5, padding: "2px 5px", borderRadius: 4, border: "1px solid transparent", cursor: "pointer", marginBottom: 2, background: c.bg, color: c.color, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "inherit", fontWeight: 600 }}>{l.name}</button>
                            );
                          })}
                          {dayLeads.length > 3 && <div style={{ fontSize: 10, color: "#64748b", paddingLeft: 2 }}>+{dayLeads.length - 3} more</div>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ============================================================
            PIVOT + GRAPH VIEWS — TEMPORARILY DISABLED.
            The CRM Dashboard (/admin-panel/crm/dashboard) already covers these analytics.
            To re-enable: restore the two branches below, the toggle buttons in the header,
            and the state declarations / memos / helpers (dimValue, measureVal, fmtVal,
            pivot, graphData, gVal, donutSegments, line geometry, ...).
            ============================================================
        <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "14px", padding: "12px 16px", borderBottom: "1px solid #e2e7e2", alignItems: "flex-end" }}>
            <FormField label="Rows">
              <select value={pivDim} onChange={(e) => setPivDim(e.target.value)} style={{ ...inputStyle, cursor: "pointer", padding: "7px 10px", minWidth: 130 }}>
                {Object.entries(DIM_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </FormField>
            <FormField label="Columns">
              <select value={pivCol} onChange={(e) => setPivCol(e.target.value)} style={{ ...inputStyle, cursor: "pointer", padding: "7px 10px", minWidth: 130 }}>
                <option value="none">— none —</option>
                {Object.entries(DIM_LABELS).filter(([k]) => k !== pivDim).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </FormField>
            <FormField label="Measure">
              <select value={pivMeasure} onChange={(e) => setPivMeasure(e.target.value)} style={{ ...inputStyle, cursor: "pointer", padding: "7px 10px", minWidth: 160 }}>
                {Object.entries(MEASURE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </FormField>
            <div style={{ fontSize: 12, color: "#94a3b8", paddingBottom: 9 }}>{canRowFilter(pivDim) ? "Click a row to apply the filter" : `${leads.length} leads`}</div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead>
                <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                  <th className="ess-th" style={{ minWidth: 170 }}>{DIM_LABELS[pivDim]}</th>
                  {pivot.cols.map((c) => <th key={c} className="ess-th ess-th-r" style={{ minWidth: 110 }}>{c}</th>)}
                  <th className="ess-th ess-th-r" style={{ minWidth: 110 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {pivot.rows.map((r) => (
                  <tr key={r.key} className="ess-tr" style={{ cursor: canRowFilter(pivDim) ? "pointer" : "default" }} onClick={() => applyRowFilter(r.key)} title={canRowFilter(pivDim) ? `Filter by ${r.key}` : ""}>
                    <td className="ess-td" style={{ fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap" }}>{r.key}</td>
                    {r.cells.map((v, i) => <td key={i} className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>{fmtVal(v, pivMeasure)}</td>)}
                    <td className="ess-td" style={{ textAlign: "right", fontWeight: 700, color: "#225f2b", whiteSpace: "nowrap" }}>{fmtVal(r.total, pivMeasure)}</td>
                  </tr>
                ))}
                <tr style={{ background: "#fafbfa", borderTop: "2px solid #e2e7e2" }}>
                  <td className="ess-td" style={{ fontWeight: 700, color: "#1e293b" }}>Total</td>
                  {pivot.colTotals.map((v, i) => <td key={i} className="ess-td" style={{ textAlign: "right", fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>{fmtVal(v, pivMeasure)}</td>)}
                  <td className="ess-td" style={{ textAlign: "right", fontWeight: 700, color: "#225f2b", whiteSpace: "nowrap" }}>{fmtVal(pivot.grandTotal, pivMeasure)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "14px", padding: "12px 16px", borderBottom: "1px solid #e2e7e2", alignItems: "flex-end" }}>
            <FormField label="Chart">
              <select value={graphType} onChange={(e) => setGraphType(e.target.value)} style={{ ...inputStyle, cursor: "pointer", padding: "7px 10px", minWidth: 110 }}>
                {Object.entries(CHART_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </FormField>
            <FormField label="Group by">
              <select value={graphGroup} onChange={(e) => setGraphGroup(e.target.value)} disabled={graphType === "line"} style={{ ...inputStyle, cursor: graphType === "line" ? "not-allowed" : "pointer", padding: "7px 10px", minWidth: 140, background: graphType === "line" ? "#f1f5f9" : "#fff" }}>
                {Object.entries(DIM_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </FormField>
            <FormField label="Value">
              <select value={graphMeasure} onChange={(e) => setGraphMeasure(e.target.value)} style={{ ...inputStyle, cursor: "pointer", padding: "7px 10px", minWidth: 150 }}>
                {Object.entries(MEASURE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </FormField>
          </div>
          <div style={{ padding: 16 }}>
            {graphData.length === 0 ? (
              <div style={{ textAlign: "center", fontSize: 13, color: "#94a3b8", padding: 24 }}>No data to display</div>
            ) : graphType === "bar" ? (
              <div style={{ display: "grid", gap: 10 }}>
                {graphData.map((d) => {
                  const v = gVal(d);
                  const pct = Math.round((v / gMax) * 100);
                  return (
                    <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 170, flexShrink: 0, fontSize: 12, fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.key}</div>
                      <div style={{ flex: 1, height: 18, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: "#225f2b", borderRadius: 4 }} />
                      </div>
                      <div style={{ width: 120, flexShrink: 0, fontSize: 12, fontWeight: 600, color: "#334155", textAlign: "right", whiteSpace: "nowrap" }}>{fmtVal(v, graphMeasure)}</div>
                    </div>
                  );
                })}
              </div>
            ) : graphType === "donut" ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center", justifyContent: "center", padding: "6px 0" }}>
                <div style={{ position: "relative", width: 180, height: 180, flexShrink: 0 }}>
                  <svg viewBox="0 0 120 120" style={{ transform: "rotate(-90deg)", width: "100%", height: "100%" }}>
                    <circle cx="60" cy="60" r="50" fill="none" stroke="#f1f5f9" strokeWidth="20" />
                    {donutSegments.map((s, i) => (
                      <circle key={i} cx="60" cy="60" r="50" fill="none" stroke={s.color} strokeWidth="20" strokeDasharray={`${s.len} ${100 - s.len}`} strokeDashoffset={-s.offset} strokeLinecap="butt" />
                    ))}
                  </svg>
                  <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>{fmtVal(gTotal, graphMeasure)}</div>
                    <div style={{ fontSize: 10, color: "#64748b" }}>{MEASURE_LABELS[graphMeasure]}</div>
                  </div>
                </div>
                <div style={{ flex: "1 1 280px", minWidth: 240, maxWidth: 460 }}>
                  {donutSegments.map((s, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 12.5 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                      <span style={{ color: "#334155", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.key}</span>
                      <span style={{ marginLeft: "auto", color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" }}>{fmtVal(s.val, graphMeasure)}</span>
                      <span style={{ color: "#94a3b8", width: 40, textAlign: "right", whiteSpace: "nowrap" }}>{s.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <svg viewBox={`0 0 ${LINE_W} ${LINE_H}`} style={{ width: "100%", height: "auto", maxWidth: 860 }}>
                {Array.from({ length: 5 }, (_, i) => {
                  const v = (gMax * 1.15 / 4) * i;
                  const yy = ly(v);
                  return (
                    <g key={i}>
                      <line x1={LINE_PAD_L} y1={yy} x2={LINE_W - LINE_PAD_R} y2={yy} stroke="#eef2f7" strokeWidth="1" />
                      <text x={LINE_PAD_L - 6} y={yy + 3.5} textAnchor="end" fontSize="10" fill="#94a3b8">{fmtVal(Math.round(v), graphMeasure)}</text>
                    </g>
                  );
                })}
                <polyline points={linePointsStr} fill="none" stroke="#225f2b" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                {graphData.map((d, i) => (
                  <g key={d.key}>
                    <circle cx={lx(i)} cy={ly(gVal(d))} r="4" fill="#225f2b" stroke="#fff" strokeWidth="1.5" />
                    <text x={lx(i)} y={LINE_H - LINE_PAD_B + 14} textAnchor="middle" fontSize="10" fill="#64748b">{shortMonth(d.key)}</text>
                  </g>
                ))}
              </svg>
            )}
          </div>
        </div>
        */
        <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
          {leads.length === 0 ? (
            <EmptyState icon={Target} title="No leads" subtitle="Adjust filters or create your first lead" />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                    {canManage && <th className="ess-th" style={{ width: 34 }}><input type="checkbox" onChange={(e) => { if (e.target.checked) { if (leads.length > 250) { toast("Cannot select all: maximum 250 leads per send. Please filter your leads or select manually.", "error"); return; } setSelectedIds(leads.map((l) => l.id)); } else { setSelectedIds([]); } }} checked={selectedIds.length > 0 && selectedIds.length === leads.length} /></th>}
                    <th className="ess-th" style={{ minWidth: "160px", cursor: "pointer" }} onClick={() => onSort("name")}>Name {sortBy === "name" && (sortDir === "asc" ? "↑" : "↓")}</th>
                    <th className="ess-th" style={{ minWidth: "110px" }}>Type</th>
                    <th className="ess-th" style={{ minWidth: "120px" }}>Stage</th>
                    <th className="ess-th" style={{ minWidth: "130px" }}>Partner</th>
                    <th className="ess-th" style={{ minWidth: "120px" }}>Owner</th>
                    <th className="ess-th" style={{ minWidth: "100px" }}>Team</th>
                    <th className="ess-th ess-th-r" style={{ minWidth: "120px", cursor: "pointer" }} onClick={() => onSort("expected_revenue")}>Expected {sortBy === "expected_revenue" && (sortDir === "asc" ? "↑" : "↓")}</th>
                    <th className="ess-th" style={{ minWidth: "100px" }}>Probability</th>
                    <th className="ess-th" style={{ minWidth: "90px" }}>Priority</th>
                    <th className="ess-th" style={{ minWidth: "140px", cursor: "pointer" }} onClick={() => onSort("date_deadline")}>Deadline {sortBy === "date_deadline" && (sortDir === "asc" ? "↑" : "↓")}</th>
                    <th className="ess-th" style={{ minWidth: "140px" }}>Next activity</th>
                    <th className="ess-th ess-th-r" style={{ minWidth: "120px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLeads.map((l, index) => {
                    const st = stageFor(l);
                    const sc = stageStyle(st || { name: "—" });
                    const tb = typeBadge(l.type);
                    const pr = Number(l.probability) || 0;
                    const prColor = PRIORITY_COLORS[l.priority] || PRIORITY_COLORS[1];
                    return (
                      <tr key={l.id} className="ess-tr" style={{ borderBottom: index < visibleLeads.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                        {canManage && (
                          <td className="ess-td">
                            <input type="checkbox" checked={selectedIds.includes(l.id)} onChange={() => toggleSelect(l.id)} />
                          </td>
                        )}
                        <td className="ess-td" style={{ fontWeight: "600", color: "#225f2b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer" }} onClick={() => openDetail(l)}>{l.name || "—"}</td>
                        <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                          <span style={{ background: tb.bg, color: tb.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{tb.label}</span>
                        </td>
                        <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ background: sc.bg, color: sc.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{st?.name || "—"}</span>
                            {(st?.is_won || st?.is_lost) && (
                              <span style={{ padding: "2px 7px", borderRadius: 5, fontSize: 10, fontWeight: 700, background: st.is_won ? "#f0f7f0" : "#fff1f2", color: st.is_won ? "#225f2b" : "#be123c", whiteSpace: "nowrap" }}>{st.is_won ? "WON" : "LOST"}</span>
                            )}
                          </div>
                        </td>
                        <td className="ess-td" style={{ color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.partner_name || "—"}</td>
                        <td className="ess-td" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            {l.user_id && <EmployeeAvatar employee={{ name: userName(l.user_id), lewoo_uid: userOf(l.user_id)?.lewoo_uid }} size={22} fontSize={9} />}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 12.5, color: "#1e293b", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{userName(l.user_id)}</div>
                              {userEmail(l.user_id) && <div style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{userEmail(l.user_id)}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{teamName(l.team_id) || "—"}</td>
                        <td className="ess-td" style={{ fontWeight: 600, color: "#1e293b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(l.expected_revenue)}</td>
                        <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1, minWidth: 46, height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ width: `${Math.min(100, pr)}%`, height: "100%", background: "#225f2b" }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#334155", minWidth: 30 }}>{pr}%</span>
                          </div>
                        </td>
                        <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                          <span style={{ color: prColor, fontWeight: 600, fontSize: 12 }}>● {PRIORITY_LABELS[l.priority] || "—"}</span>
                        </td>
                        <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{l.date_deadline ? fmt.date(l.date_deadline) : "—"}</td>
                        <td className="ess-td" style={{ color: "#b45309", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: 12 }}>
                          {l.next_activity ? fmt.date(l.next_activity.date_deadline) : "—"}
                        </td>
                        <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <button title="Actions" onClick={() => setOpenActions(openActions === l.id ? null : l.id)} style={iconBtn("#475569")}>
                              <MoreVertical size={14} />
                            </button>
                            {openActions === l.id && (
                              <>
                                <div onClick={() => setOpenActions(null)} style={{ position: "fixed", inset: 0, zIndex: 20 }} />
                                <div style={{ position: "absolute", right: 0, top: "100%", zIndex: 21, background: "#fff", border: "1px solid #e2e7e2", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", minWidth: 190, marginTop: 4, overflow: "hidden", padding: "4px 0" }}>
                                  <button onClick={() => { setOpenActions(null); openDetail(l); }} style={menuItem}><Eye size={13} /> View</button>
                                  {canManage && !isTerminal(l) && (
                                    <>
                                      {l.type === "lead"
                                        ? <button onClick={() => { setOpenActions(null); setActionTarget({ action: "convert", lead: l }); }} style={menuItem}><Split size={13} /> Convert to opportunity</button>
                                        : <button onClick={() => { setOpenActions(null); setActionTarget({ action: "won", lead: l }); }} style={menuItem}><Check size={13} /> Mark Won</button>}
                                      {l.type === "opportunity" && <button onClick={() => { setOpenActions(null); setActionTarget({ action: "lost", lead: l }); }} style={menuItem}><X size={13} /> Mark Lost</button>}
                                      <button onClick={() => { setOpenActions(null); setActionTarget({ action: "assign", lead: l }); }} style={menuItem}><UserPlus size={13} /> Assign</button>
                                       {l.type === "opportunity" && <button onClick={() => { setOpenActions(null); setActionTarget({ action: "quotation", lead: l }); }} style={menuItem}><FileText size={13} /> Create quotation</button>}
                                        {(l.phone || l.mobile || l.email_from) && (
                                        <>
                                          <div style={{ borderTop: "1px solid #f1f3f0", margin: "4px 8px" }} />
                                          {l.phone && <a href={`tel:${l.phone}`} style={{ ...menuItem, textDecoration: "none", color: "inherit", display: "flex" }}><Phone size={13} /> Call</a>}
                                          {(l.phone || l.mobile) && <a href={`https://wa.me/${(l.phone || l.mobile).replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" style={{ ...menuItem, textDecoration: "none", color: "inherit", display: "flex" }}><MessageCircle size={13} /> WhatsApp</a>}
                                          {(l.phone || l.mobile) && <button onClick={() => { setOpenActions(null); openSingleWaRelance(l); }} style={menuItem}><MessageCircle size={13} /> Relancer via WhatsApp</button>}
                                          {l.email_from && <a href={`mailto:${l.email_from}`} style={{ ...menuItem, textDecoration: "none", color: "inherit", display: "flex" }}><Mail size={13} /> Email</a>}
                                        </>
                                      )}
                                       <div style={{ borderTop: "1px solid #f1f3f0", margin: "4px 8px" }} />
                                      <button onClick={() => { setOpenActions(null); openEdit(l); }} style={menuItem}><Pencil size={13} /> Edit</button>
                                      <button onClick={() => { setOpenActions(null); handleDelete(l); }} style={{ ...menuItem, color: "#be123c" }}><Trash2 size={13} /> Delete</button>
                                    </>
                                  )}
                                  {canManage && isTerminal(l) && stageFor(l)?.is_lost && (
                                    <button onClick={() => { setOpenActions(null); handleReopen(l); }} style={menuItem}><RotateCcw size={13} /> Reopen</button>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e2e7e2", background: "#fafbfa" }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={pageBtn(page <= 1)}>← Prev</button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return <button key={p} onClick={() => setPage(p)} style={{ ...pageBtn(false), background: p === page ? "#225f2b" : "#fff", color: p === page ? "#fff" : "#334155", fontWeight: p === page ? 600 : 500 }}>{p}</button>;
              })}
              <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={pageBtn(page >= totalPages)}>Next →</button>
            </div>
          </div>
        </div>
      )}

      {/* Création / édition */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title={editing ? `Edit Lead — ${editing.name || editing.id}` : "New Lead"} size="xl">
        <div>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#225f2b", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <Target size={13} /> Essentials
            </div>
            <FormField label="Lead Name" required>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Lead Client X" style={inputStyle} />
            </FormField>
            <div style={{ display: "grid", gap: "14px" }} className="ess-rg-2">
              <FormField label="Type">
                <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} style={inputStyle}>
                  <option value="lead">Lead</option>
                  <option value="opportunity">Opportunity</option>
                </select>
              </FormField>
              <FormField label="Stage">
                <select value={form.stage_id} onChange={(e) => setForm((f) => ({ ...f, stage_id: e.target.value }))} style={inputStyle}>
                  <option value="">Auto (first non-terminal)…</option>
                  {stages.filter((s) => !s.is_won && !s.is_lost).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </FormField>
            </div>
              <FormField label="Company">
                <input value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} placeholder="Company name" style={inputStyle} />
              </FormField>
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#225f2b", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <UserPlus size={13} /> Commercial
            </div>
            <div style={{ display: "grid", gap: "14px" }} className="ess-rg-2">
              <FormField label="Owner (Salesperson)">
                <SearchSelect
                  id="ownerSelect"
                  options={users.map((u) => ({ value: u.id, label: u.name || u.username || u.email || u.login, sub: u.email || "", avatar: { id: u.id, name: u.name || u.username || u.email || u.login } }))}
                  value={form.user_id ?? ""}
                  placeholder="Unassigned…"
                  emptyLabel="Unassigned"
                  avatarField="avatar"
                  onChange={(v) => setForm((f) => ({ ...f, user_id: v }))}
                />
              </FormField>
              <FormField label="Team">
                <select value={form.team_id} onChange={(e) => setForm((f) => ({ ...f, team_id: e.target.value }))} style={inputStyle}>
                  <option value="">No team…</option>
                  {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </FormField>
              <FormField label="Source">
                <select value={form.source_id} onChange={(e) => setForm((f) => ({ ...f, source_id: e.target.value }))} style={inputStyle}>
                  <option value="">None…</option>
                  {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </FormField>
              <FormField label="Medium">
                <select value={form.medium_id} onChange={(e) => setForm((f) => ({ ...f, medium_id: e.target.value }))} style={inputStyle}>
                  <option value="">None…</option>
                  {mediums.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </FormField>
              <FormField label="Priority">
                <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} style={inputStyle}>
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </FormField>
              <FormField label="Campaign">
                <select value={form.campaign_id} onChange={(e) => setForm((f) => ({ ...f, campaign_id: e.target.value }))} style={inputStyle}>
                  <option value="">None…</option>
                  {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </FormField>
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#225f2b", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <Banknote size={13} /> Financial
            </div>
            <div style={{ display: "grid", gap: "14px" }} className="ess-rg-2">
              <FormField label="Expected Revenue (FCFA)">
                <input type="number" min="0" value={form.expected_revenue} onChange={(e) => setForm((f) => ({ ...f, expected_revenue: e.target.value }))} placeholder="0" style={inputStyle} />
              </FormField>
              <FormField label="Plan Revenue (FCFA)">
                <input type="number" min="0" value={form.plan_revenue} onChange={(e) => setForm((f) => ({ ...f, plan_revenue: e.target.value }))} placeholder="0" style={inputStyle} />
              </FormField>
              <FormField label="Probability (%)">
                <input type="number" min="0" max="100" value={form.probability} onChange={(e) => setForm((f) => ({ ...f, probability: e.target.value }))} placeholder="0" style={inputStyle} />
              </FormField>
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#225f2b", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <CalendarDays size={13} /> Schedule
            </div>
            <div style={{ display: "grid", gap: "14px" }} className="ess-rg-2">
              <FormField label="Open Date">
                <input type="date" value={form.date_open} onChange={(e) => setForm((f) => ({ ...f, date_open: e.target.value }))} style={inputStyle} />
              </FormField>
              <FormField label="Deadline">
                <input type="date" value={form.date_deadline} onChange={(e) => setForm((f) => ({ ...f, date_deadline: e.target.value }))} style={inputStyle} />
              </FormField>
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#225f2b", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <Mail size={13} /> Contact & Details
            </div>
            <div style={{ display: "grid", gap: "14px" }} className="ess-rg-2">
              <FormField label="Email">
                <input type="email" value={form.email_from} onChange={(e) => setForm((f) => ({ ...f, email_from: e.target.value }))} placeholder="contact@example.com" style={inputStyle} />
              </FormField>
              <FormField label="Phone">
                <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+237…" style={inputStyle} />
              </FormField>
              <FormField label="Mobile">
                <input value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} placeholder="+237…" style={inputStyle} />
              </FormField>
            </div>
            <FormField label="Tags">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, border: "1px solid #e2e7e2", borderRadius: 6, padding: 8, maxHeight: 110, overflowY: "auto" }}>
                {tags.length === 0 && <span style={{ fontSize: 12, color: "#94a3b8" }}>No tags defined</span>}
                {tags.map((t) => {
                  const active = (form.tag_ids || []).includes(t.id);
                  return (
                    <button key={t.id} type="button" onClick={() => toggleTag(t.id)}
                      style={{ padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600, cursor: "pointer", border: active ? "1px solid #225f2b" : "1px solid #e2e7e2", background: active ? "#f0f7f0" : "#fff", color: active ? "#225f2b" : "#64748b" }}>
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </FormField>
            <FormField label="Description">
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Context, needs…" rows={3} style={{ ...inputStyle, resize: "vertical" }} />
            </FormField>
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
            <button onClick={() => setShowNew(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button onClick={editing ? handleSave : handleCreate} disabled={creating} className="ess-btn ess-btn-primary">
              {creating ? "Saving…" : editing ? "Update Lead" : "Create Lead"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Détail */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? (detail.name || `LEAD ${detail.id}`) : ""} size="xl">
        {detail && (
          <div>
            {detailLoading ? <LoadingScreen /> : (
              <>
                <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e2e7e2", marginBottom: 16, overflowX: "auto" }}>
                  {[
                    { key: "overview", label: "Overview", icon: Eye },
                    { key: "activities", label: "Activities", icon: Activity, count: detail.activities?.length },
                    { key: "timeline", label: "Timeline", icon: MessageSquare, count: detail.messages?.length },
                    { key: "attachments", label: "Attachments", icon: Paperclip, count: detail.attachments?.length },
                   { key: "quotes", label: "Quotes", icon: FileText, count: detail.orders?.length },
                   { key: "products", label: "Products", icon: Package },
                   ].map((t) => (
                    <button
                      key={t.key}
                      onClick={() => {
                        setDetailTab(t.key);
                        if (t.key === "timeline") loadHistory();
                      }}
                      style={{
                        display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", fontSize: 13, fontWeight: 500,
                        border: "none", borderBottom: detailTab === t.key ? "2px solid #225f2b" : "2px solid transparent",
                        background: "transparent", cursor: "pointer", whiteSpace: "nowrap",
                        color: detailTab === t.key ? "#225f2b" : "#64748b",
                      }}
                    >
                      <t.icon size={14} /> {t.label}
                      {t.count !== undefined && t.count > 0 && <span style={{ background: "#eef6f3", color: "#0f766e", fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 10 }}>{t.count}</span>}
                    </button>
                  ))}
                </div>

                {detailTab === "overview" && (
                  <>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
                      {(() => { const tb = typeBadge(detail.type); return <span style={{ background: tb.bg, color: tb.text, padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{tb.label}</span>; })()}
                      {(() => { const sc = stageStyle(stageFor(detail) || { name: "—" }); return <span style={{ background: sc.bg, color: sc.text, padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{stageFor(detail)?.name || "—"}</span>; })()}
                      <span style={{ color: PRIORITY_COLORS[detail.priority] || PRIORITY_COLORS[1], padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>● {PRIORITY_LABELS[detail.priority] || "—"}</span>
                    </div>
                    <div style={{ display: "grid", gap: "12px", marginBottom: "18px" }} className="ess-rg-3">
                      <div style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: 8, padding: 14, textAlign: "center" }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: "#1e293b" }}>{fmt.money(detail.expected_revenue)}</div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>Expected revenue</div>
                      </div>
                      <div style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: 8, padding: 14, textAlign: "center" }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: "#225f2b" }}>{Number(detail.probability) || 0}%</div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>Probability</div>
                      </div>
                      <div style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: 8, padding: 14, textAlign: "center" }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: "#b45309" }}>{detail.won_revenue ? fmt.money(detail.won_revenue) : "—"}</div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>Won revenue</div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gap: "12px", marginBottom: "18px" }} className="ess-rg-2">
                      <DetailField label="Company">{detail.company}</DetailField>
                      <DetailField label="Partner">{detail.partner_name}</DetailField>
                      <DetailField label="Owner">
                        {userName(detail.user_id)}
                        {userEmail(detail.user_id) && <span style={{ color: "#94a3b8", fontSize: 11, fontWeight: 400 }}> · {userEmail(detail.user_id)}</span>}
                      </DetailField>
                      <DetailField label="Team">{detail.team_id ? teamName(detail.team_id) : "—"}</DetailField>
                      <DetailField label="Source">{detail.source_name}</DetailField>
                      <DetailField label="Email">{detail.email_from ? <a href={`mailto:${detail.email_from}`} style={{ color: "#225f2b", textDecoration: "none" }}>{detail.email_from}</a> : "—"}</DetailField>
                      <DetailField label="Phone">
                        {detail.phone || detail.mobile ? (
                          <span style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
                            {(detail.phone || detail.mobile) && <a href={`tel:${detail.phone || detail.mobile}`} style={{ color: "#225f2b", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}><Phone size={13} /> {detail.phone || detail.mobile}</a>}
                            {(detail.phone || detail.mobile) && <a href={`https://wa.me/${(detail.phone || detail.mobile).replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" style={{ color: "#225f2b", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}><MessageCircle size={13} /> WhatsApp</a>}
                          </span>
                        ) : "—"}
                      </DetailField>
                      <DetailField label="Opened">{detail.date_open ? fmt.date(detail.date_open) : "—"}</DetailField>
                      <DetailField label="Deadline">{detail.date_deadline ? fmt.date(detail.date_deadline) : "—"}</DetailField>
                      <DetailField label="Closed">{detail.date_closed ? fmt.date(detail.date_closed) : "—"}</DetailField>
                    </div>
                    {detail.tags?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 14 }}>
                        {detail.tags.map((t) => <span key={t} style={{ background: "#eef6f3", color: "#0f766e", padding: "3px 10px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{t}</span>)}
                      </div>
                    )}
                    {detail.description && <DetailField label="Description" full>{detail.description}</DetailField>}
                    {detail.last_activity && (
                      <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "#f8faf8", border: "1px solid #e2e7e2", fontSize: 12, color: "#64748b" }}>
                        <strong>Last activity:</strong> {detail.last_activity.type_name} — {detail.last_activity.summary || ""} ({fmt.date(detail.last_activity.date_done)})
                      </div>
                    )}
                    {detail.next_activity && (
                      <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 12, color: "#b45309" }}>
                        <strong>Next activity:</strong> {detail.next_activity.type_name} — {detail.next_activity.summary || ""} (due {fmt.date(detail.next_activity.date_deadline)})
                      </div>
                    )}
                  </>
                )}

                {detailTab === "activities" && (
                  <>
                    {canManage && (
                      <div style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: 8, padding: 14, marginBottom: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                          <CalendarPlus size={15} style={{ color: "#225f2b" }} />
                          <strong style={{ fontSize: 13, color: "#1e293b" }}>Schedule activity</strong>
                        </div>
                        <div style={{ display: "grid", gap: "12px" }} className="ess-rg-2">
                          <FormField label="Type">
                            <select value={newActivity.activity_type_id} onChange={(e) => setNewActivity((f) => ({ ...f, activity_type_id: e.target.value }))} style={inputStyle}>
                              <option value="">Select…</option>
                              {activityTypes.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                          </FormField>
                          <FormField label="Assigned to">
                            <select value={newActivity.user_id} onChange={(e) => setNewActivity((f) => ({ ...f, user_id: e.target.value }))} style={inputStyle}>
                              <option value="">Unassigned…</option>
                              {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.username || u.email || u.login}</option>)}
                            </select>
                          </FormField>
                          <FormField label="Due date">
                            <input type="date" value={newActivity.date_deadline} onChange={(e) => setNewActivity((f) => ({ ...f, date_deadline: e.target.value }))} style={inputStyle} />
                          </FormField>
                          <FormField label="Summary">
                            <input value={newActivity.summary} onChange={(e) => setNewActivity((f) => ({ ...f, summary: e.target.value }))} placeholder="e.g. Call to discuss pricing" style={inputStyle} />
                          </FormField>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <button disabled={actionBusy} onClick={handleAddActivity} className="ess-btn ess-btn-primary">
                            <Plus size={14} /> Add activity
                          </button>
                        </div>
                      </div>
                    )}
                    {(detail.activities || []).length === 0 ? (
                      <EmptyState icon={Calendar} title="No activities" subtitle="Schedule a follow-up, call or meeting" />
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {(detail.activities || []).map((a) => {
                          const isOverdue = !a.done && a.date_deadline && new Date(a.date_deadline) < new Date();
                          return (
                            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: "1px solid #e2e7e2", borderRadius: 8, background: a.done ? "#fafbfa" : "#fff" }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{a.summary || "—"}</div>
                                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                  {a.type_name} · {a.user_name || "Unassigned"} · due {fmt.date(a.date_deadline)}
                                  {a.done && ` · done ${fmt.date(a.date_done)}`}
                                  {isOverdue && <span style={{ color: "#be123c", fontWeight: 700 }}> · OVERDUE</span>}
                                </div>
                              </div>
                              {canManage && !a.done && (
                                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                                  <button title="Mark done" onClick={() => handleDoneActivity(a)} style={cardBtn("#225f2b")}><Check size={12} /> Done</button>
                                  <button title="Snooze" onClick={() => handleSnoozeActivity(a)} style={cardBtn("#b45309")}><BellRing size={12} /> Snooze</button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {detailTab === "timeline" && (
                  <>
                    {canManage && (
                      <div style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: 8, padding: 14, marginBottom: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                          <MessageSquare size={15} style={{ color: "#225f2b" }} />
                          <strong style={{ fontSize: 13, color: "#1e293b" }}>Add note / email / call</strong>
                        </div>
                        <div style={{ display: "grid", gap: "12px" }} className="ess-rg-2">
                          <FormField label="Type">
                            <select value={newMessage.type} onChange={(e) => setNewMessage((f) => ({ ...f, type: e.target.value }))} style={inputStyle}>
                              <option value="note">Note</option>
                              <option value="email">Email</option>
                              <option value="call">Call</option>
                              <option value="whatsapp">WhatsApp</option>
                            </select>
                          </FormField>
                          <FormField label="Body">
                            <textarea value={newMessage.body} onChange={(e) => setNewMessage((f) => ({ ...f, body: e.target.value }))} rows={2} placeholder="Write a message…" style={{ ...inputStyle, resize: "vertical" }} />
                          </FormField>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <button disabled={actionBusy} onClick={handleAddMessage} className="ess-btn ess-btn-primary">
                            <Plus size={14} /> Add message
                          </button>
                        </div>
                      </div>
                    )}
                    {(detail.messages || []).length === 0 && history.length === 0 ? (
                      <EmptyState icon={Inbox} title="No messages" subtitle="Add notes, emails, calls or WhatsApp messages to the timeline" />
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {(detail.messages || []).map((m) => (
                          <div key={m.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "9px 12px", border: "1px solid #e2e7e2", borderRadius: 8 }}>
                             <span style={{ background: m.type === "call" ? "#fffbeb" : m.type === "email" ? "#eff6ff" : m.type === "whatsapp" ? "#f0fdf4" : "#eef6f3", color: m.type === "call" ? "#b45309" : m.type === "email" ? "#2563eb" : m.type === "whatsapp" ? "#166534" : "#0f766e", padding: "2px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700, textTransform: "uppercase", flexShrink: 0, marginTop: 2 }}>{m.type || "note"}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, color: "#334155" }}>{m.body || "—"}</div>
                              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{m.author_name || ""}{m.create_date ? ` · ${fmt.dateTime(m.create_date)}` : ""}</div>
                            </div>
                            {canManage && (
                              <button title="Delete" onClick={() => handleDeleteMessage(m)} style={{ ...iconBtn("#be123c"), padding: "4px 6px" }}><Trash2 size={12} /></button>
                            )}
                          </div>
                        ))}
                        {history.length > 0 && (
                          <div style={{ marginTop: 8, paddingTop: 12, borderTop: "1px solid #e2e7e2" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                              <History size={14} style={{ color: "#64748b" }} />
                              <strong style={{ fontSize: 12, color: "#64748b" }}>History</strong>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {history.map((h, i) => (
                                <div key={i} style={{ fontSize: 12, color: "#64748b" }}>
                                  <span style={{ textTransform: "capitalize", fontWeight: 600, color: "#475569" }}>{h.action.replace(/_/g, " ")}</span>
                                  {h.stage_name && ` → ${h.stage_name}`}
                                  {h.from_stage_name && ` (from ${h.from_stage_name})`}
                                  <span> · {h.actor_login || ""} · {fmt.dateTime(h.created_at)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {detailTab === "attachments" && (
                  <>
                    {canManage && (
                      <div style={{ marginBottom: 16 }}>
                        <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={handleUploadFile} />
                        <button disabled={uploading} onClick={() => fileInputRef.current?.click()} className="ess-btn ess-btn-primary">
                          <Upload size={14} /> {uploading ? "Uploading…" : "Upload file"}
                        </button>
                      </div>
                    )}
                    {(detail.attachments || []).length === 0 ? (
                      <EmptyState icon={Paperclip} title="No attachments" subtitle="Upload contracts, quotes, ID documents…" />
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {(detail.attachments || []).map((att) => (
                          <div key={att.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", border: "1px solid #e2e7e2", borderRadius: 8 }}>
                            <Paperclip size={15} style={{ color: "#64748b", flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.name || att.file_name || "—"}</div>
                              {att.create_date && <div style={{ fontSize: 11, color: "#94a3b8" }}>{fmt.dateTime(att.create_date)}</div>}
                            </div>
                            <button title="Download" onClick={() => api.crm.downloadAttachment(detail.id, att.id, att.file_name || att.name).then(() => toast("Download started", "success")).catch((e) => toast(e.message, "error"))} style={iconBtn("#225f2b")}><Download size={13} /></button>
                            {canManage && <button title="Delete" onClick={() => handleDeleteAttachment(att)} style={iconBtn("#be123c")}><Trash2 size={13} /></button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {detailTab === "quotes" && (
                  (detail.orders || []).length === 0 ? (
                    <EmptyState icon={FileText} title="No quotations" subtitle={canManage ? 'Use "Create quotation" to generate a draft sales order' : "No linked quotations"} />
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(detail.orders || []).map((o) => (
                        <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid #e2e7e2", borderRadius: 8 }}>
                          <FileText size={15} style={{ color: "#225f2b", flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#225f2b" }}>{o.name}</div>
                            <div style={{ fontSize: 11, color: "#64748b" }}>{fmt.money(o.amount_total)} · {o.state}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}

                {detailTab === "products" && (
                  <>
                    {canManage && (
                      <div style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "flex-end" }}>
                        <FormField label="Add product" style={{ marginBottom: 0, flex: 1 }}>
                          <select id="leadProductSelect" style={inputStyle}>
                            <option value="">Select a product…</option>
                            {allProducts.filter((p) => !detailProducts.find((dp) => dp.product_id === p.id)).map((p) => (
                              <option key={p.id} value={p.id}>{p.name}{p.default_code ? ` (${p.default_code})` : ""}</option>
                            ))}
                          </select>
                        </FormField>
                        <button disabled={addingProduct} onClick={async () => {
                          const sel = document.getElementById("leadProductSelect");
                          const pid = sel?.value;
                          if (!pid) return;
                          setAddingProduct(true);
                          try {
                            const prod = allProducts.find((p) => String(p.id) === String(pid));
                            await api.crm.leadProducts.add(detail.id, {
                              product_id: parseInt(pid),
                              product_name: prod?.name || "",
                              price_unit: prod?.list_price || 0,
                              product_uom_qty: 1,
                              product_uom: prod?.uom_name || "",
                              discount: 0,
                            });
                            toast("Product added", "success");
                            const prods = await api.crm.leadProducts.list(detail.id, { limit: 100 }).catch(() => []);
                            const n = norm(prods);
                            setDetailProducts(Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : []);
                            sel.value = "";
                          } catch (e) {
                            toast(e.message || "Add product failed", "error");
                          }
                          setAddingProduct(false);
                        }} className="ess-btn ess-btn-primary">
                          {addingProduct ? "Adding…" : <><Plus size={14} /> Add</>}
                        </button>
                      </div>
                    )}
                    {(detailProducts || []).length === 0 ? (
                      <EmptyState icon={Package} title="No products linked" subtitle={canManage ? "Add products to prepare quotations for this lead" : "No products linked to this lead"} />
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {detailProducts.map((lp) => (
                          <div key={lp.id || lp.product_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid #e2e7e2", borderRadius: 8, flexWrap: "wrap" }}>
                            <Package size={15} style={{ color: "#225f2b", flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{lp.product_name || `Product #${lp.product_id}`}</div>
                              <div style={{ fontSize: 11, color: "#64748b" }}>
                                Qty: {lp.product_uom_qty || 1} · Unit: {fmt.money(lp.price_unit || 0)} · Disc: {lp.discount || 0}% · Subtotal: {fmt.money((Number(lp.product_uom_qty) || 1) * (Number(lp.price_unit) || 0) * (1 - (Number(lp.discount) || 0) / 100))}
                              </div>
                            </div>
                            {canManage && (
                              <button title="Remove" onClick={async () => {
                                try {
                                  await api.crm.leadProducts.remove(detail.id, lp.product_id);
                                  toast("Product removed", "success");
                                  setDetailProducts((prev) => prev.filter((p) => p.product_id !== lp.product_id));
                                } catch (e) {
                                  toast(e.message || "Remove failed", "error");
                                }
                              }} style={{ ...iconBtn("#be123c"), padding: "4px 6px" }}><Trash2 size={12} /></button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px", marginTop: "16px" }}>
                  {canManage && detail.type === "lead" && !isTerminal(detail) && (
                    <button onClick={() => setActionTarget({ action: "convert", lead: detail })} className="ess-btn ess-btn-secondary">
                      <Split size={14} /> Convert
                    </button>
                  )}
                  {canManage && detail.type === "opportunity" && !isTerminal(detail) && (
                    <>
                      <button onClick={() => setActionTarget({ action: "won", lead: detail })} className="ess-btn ess-btn-primary">
                        <Check size={14} /> Mark Won
                      </button>
                      <button onClick={() => setActionTarget({ action: "lost", lead: detail })} className="ess-btn ess-btn-secondary" style={{ color: "#be123c" }}>
                        <X size={14} /> Mark Lost
                      </button>
                      <button onClick={() => setActionTarget({ action: "quotation", lead: detail })} className="ess-btn ess-btn-secondary">
                        <FileText size={14} /> Create quotation
                      </button>
                    </>
                  )}
                  {canManage && (
                    <button onClick={() => setActionTarget({ action: "assign", lead: detail })} className="ess-btn ess-btn-secondary">
                      <UserPlus size={14} /> Assign
                    </button>
                  )}
                  {canManage && <button onClick={() => openEdit(detail)} className="ess-btn ess-btn-primary"><Pencil size={14} /> Edit</button>}
                  {canManage && <button onClick={() => handleDelete(detail)} className="ess-btn ess-btn-secondary" style={{ color: "#be123c" }}><Trash2 size={14} /> Delete</button>}
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Actions métier */}
      <Modal
        open={!!actionTarget}
        onClose={() => setActionTarget(null)}
        title={actionLead ? `${actionTarget.action === "convert" ? "Convert" : actionTarget.action === "won" ? "Mark Won" : actionTarget.action === "lost" ? "Mark Lost" : actionTarget.action === "quotation" ? "Create Quotation" : "Assign"} — ${actionLead.name || actionLead.id}` : ""}
      >
        {actionLead && (
          <div>
            {actionTarget.action === "convert" && (
              <div style={{ fontSize: 14, color: "#475569", lineHeight: 1.8 }}>
                <p>Convert <strong>{actionLead.name}</strong> to an opportunity?</p>
                <p style={{ fontSize: 13, color: "#64748b" }}>A partner will be created or linked automatically (by email/company). The lead becomes an opportunity and appears in the pipeline.</p>
              </div>
            )}
            {actionTarget.action === "won" && (
              <FormField label="Won amount (FCFA)">
                <input type="number" min="0" defaultValue={actionLead.expected_revenue ?? ""} id="wonAmount" style={inputStyle} />
                <p style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>Default: expected revenue. Stored in won_revenue (does not overwrite expected_revenue).</p>
              </FormField>
            )}
            {actionTarget.action === "lost" && (
              <FormField label="Lost reason" required>
                <select id="lostReason" style={inputStyle} defaultValue="">
                  <option value="">Select a reason…</option>
                  {lostReasons.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </FormField>
            )}
            {actionTarget.action === "assign" && (
              <div style={{ display: "grid", gap: "14px" }} className="ess-rg-2">
                <FormField label="Salesperson">
                  <SearchSelect
                    id="assignUser"
                    value={actionLead.user_id ?? ""}
                    placeholder="Select a salesperson…"
                    emptyLabel="Unassigned"
                    options={users.map((u) => ({ value: u.id, label: u.name || u.username || u.email || u.login, sub: u.email || "", avatar: { id: u.id, name: u.name || u.username || u.email || u.login } }))}
                    avatarField="avatar"
                  />
                </FormField>
                <FormField label="Team">
                  <select id="assignTeam" style={inputStyle} defaultValue={actionLead.team_id ?? ""}>
                    <option value="">No team…</option>
                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </FormField>
              </div>
            )}
            {actionTarget.action === "quotation" && !createdQuotation && (
              <div>
                <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, marginBottom: 12 }}>
                  <p>Créer un devis brouillon (SO) depuis <strong>{actionLead.name}</strong> ? L'aperçu ci-dessous utilise les <em>produits liés au lead</em> ou à défaut une ligne « Opportunité — {actionLead.name} ».</p>
                </div>
                {!quotPreview ? (
                  <button disabled={quotPreviewLoading} onClick={() => handleQuotPreview(actionLead)} className="ess-btn ess-btn-secondary" style={{ marginBottom: 12 }}>
                    {quotPreviewLoading ? "Chargement…" : <><Eye size={14} /> Aperçu du devis</>}
                  </button>
                ) : (
                  <div style={{ border: "1px solid #e2e7e2", borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
                    <div style={{ padding: "10px 14px", background: "#f8fafc", borderBottom: "1px solid #e2e7e2", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <strong style={{ fontSize: 13, color: "#1e293b" }}>{quotPreview.name || "SO/BROUILLON"}</strong>
                      <span style={{ fontSize: 12, color: "#64748b" }}>{quotPreview.partner_name || actionLead.partner_name || "—"} · {fmt.money(quotPreview.amount_total)}</span>
                    </div>
                    <div style={{ maxHeight: 180, overflowY: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}><th style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, color: "#64748b" }}>Produit</th><th style={{ textAlign: "right", padding: "6px 10px", fontSize: 11, color: "#64748b" }}>Qté</th><th style={{ textAlign: "right", padding: "6px 10px", fontSize: 11, color: "#64748b" }}>PU</th><th style={{ textAlign: "right", padding: "6px 10px", fontSize: 11, color: "#64748b" }}>Total</th></tr></thead>
                        <tbody>
                          {(quotPreview.lines || []).map((l, i) => (
                            <tr key={i} style={{ borderBottom: i < (quotPreview.lines || []).length - 1 ? "1px solid #f1f5f9" : "none" }}>
                              <td style={{ padding: "6px 10px", fontSize: 12, color: "#334155" }}>{l.name || l.product_name || "—"}</td>
                              <td style={{ padding: "6px 10px", fontSize: 12, textAlign: "right" }}>{l.product_uom_qty ?? 1}</td>
                              <td style={{ padding: "6px 10px", fontSize: 12, textAlign: "right" }}>{fmt.money(l.price_unit)}</td>
                              <td style={{ padding: "6px 10px", fontSize: 12, textAlign: "right", fontWeight: 600 }}>{fmt.money((Number(l.product_uom_qty) || 1) * (Number(l.price_unit) || 0))}</td>
                            </tr>
                          ))}
                          {(quotPreview.lines || []).length === 0 && <tr><td colSpan={4} style={{ padding: 12, textAlign: "center", fontSize: 12, color: "#94a3b8" }}>Aucune ligne — une ligne fallback sera créée</td></tr>}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ padding: "8px 14px", background: "#f0f7f0", textAlign: "right", fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Total {fmt.money(quotPreview.amount_total)}</div>
                  </div>
                )}
              </div>
            )}
            {actionTarget.action === "quotation" && createdQuotation && (
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#166534", marginBottom: 6 }}>✓ Devis {createdQuotation.name} créé</div>
                <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>Montant {fmt.money(createdQuotation.amount_total)} · {createdQuotation.state || "draft"} — envoyez-le directement :</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button disabled={quotSendBusy} onClick={() => handleSendCreatedQuotation("whatsapp")} className="ess-btn ess-btn-primary" style={{ background: "#166534" }}><MessageCircle size={14} /> {quotSendBusy ? "Envoi…" : "Envoyer par WhatsApp"}</button>
                  <button disabled={quotSendBusy} onClick={() => handleSendCreatedQuotation("email")} className="ess-btn ess-btn-secondary"><Mail size={14} /> Envoyer par Email</button>
                  <a href="/admin-panel/sales" style={{ marginLeft: "auto", fontSize: 12, color: "#225f2b", fontWeight: 600, alignSelf: "center", textDecoration: "none" }}>Voir dans Ventes →</a>
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px", marginTop: "12px" }}>
              <button onClick={() => { setActionTarget(null); setQuotPreview(null); setCreatedQuotation(null); }} className="ess-btn ess-btn-secondary">Fermer</button>
              {actionTarget.action === "quotation" && !createdQuotation && (
                <button
                  disabled={actionBusy}
                  className="ess-btn ess-btn-primary"
                  onClick={async () => { await runAction("quotation", actionLead); }}
                >
                  {actionBusy ? "Création…" : "Créer le devis"}
                </button>
              )}
              {actionTarget.action !== "quotation" && (
                <button
                  disabled={actionBusy}
                  className="ess-btn ess-btn-primary"
                  onClick={async () => {
                    const a = actionTarget.action;
                    let body;
                    if (a === "won") {
                      const v = parseFloat(document.getElementById("wonAmount")?.value);
                      if (!Number.isNaN(v)) body = { won_amount: v };
                    }
                    if (a === "lost") {
                      const id = document.getElementById("lostReason")?.value;
                      if (!id) { toast("Lost reason is required", "error"); return; }
                      body = { lost_reason_id: parseInt(id) };
                    }
                    if (a === "assign") {
                      const uid = document.getElementById("assignUser")?.value;
                      const tid = document.getElementById("assignTeam")?.value;
                      if (!uid && !tid) { toast("Choose a salesperson or a team", "error"); return; }
                      body = { user_id: uid ? parseInt(uid) : undefined, team_id: tid ? parseInt(tid) : undefined };
                    }
                    await runAction(a, actionLead, body);
                  }}
                >
                  {actionBusy ? "Working…" : "Confirm"}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Merge */}
      <Modal open={!!mergeTarget} onClose={() => setMergeTarget(null)} title="Merge duplicates">
        {mergeTarget && (
          <div>
            <p style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>
              Choose the <strong>main lead</strong> that will keep the data. The others will be merged into it and soft-deleted.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {[...(mergeTarget.others || []).map((id) => leads.find((l) => l.id === id)).filter(Boolean), leads.find((l) => l.id === mergeTarget.principal)].map((l) => {
                const isPrincipal = l.id === mergeTarget.principal;
                return (
                  <label key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: isPrincipal ? "1px solid #225f2b" : "1px solid #e2e7e2", borderRadius: 8, background: isPrincipal ? "#f0f7f0" : "#fff", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="mergePrincipal"
                      checked={isPrincipal}
                      onChange={() => setMergeTarget((m) => ({ ...m, principal: l.id, others: (m.others || []).filter((x) => x !== l.id).concat(m.principal && m.principal !== l.id ? [m.principal] : []) }))}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{l.name || `#${l.id}`} {isPrincipal && <span style={{ color: "#225f2b", fontSize: 11, fontWeight: 700 }}>(MAIN)</span>}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{stageFor(l)?.name || "—"} · {fmt.money(l.expected_revenue)}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
              <button onClick={() => setMergeTarget(null)} className="ess-btn ess-btn-secondary">Cancel</button>
              <button disabled={actionBusy} onClick={handleMerge} className="ess-btn ess-btn-primary">
                {actionBusy ? "Merging…" : `Merge ${mergeTarget.others?.length || 0} into main`}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk email */}
      <Modal open={mailOpen} onClose={() => setMailOpen(false)} title={`Send Email — ${selectedIds.length} lead${selectedIds.length === 1 ? "" : "s"}`} size="lg">
        <div>
          <FormField label="Template" required>
            {mailTemplates.length === 0 ? (
              <div style={{ fontSize: 13, color: "#94a3b8", padding: "9px 0" }}>Loading templates…</div>
            ) : (
              <select value={mailTemplateId} onChange={(e) => setMailTemplateId(e.target.value)} style={inputStyle}>
                <option value="">Select a template…</option>
                {mailTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.attach_documents ? " 📎" : ""}</option>)}
              </select>
            )}
          </FormField>
          {(() => {
            const tpl = mailTemplates.find((t) => String(t.id) === String(mailTemplateId));
            if (!tpl) return null;
            return (
              <>
                <div style={{ fontSize: 13, color: "#334155", marginBottom: 8 }}>
                  Subject: <strong>{tpl.subject || "—"}</strong>
                </div>
                <div
                  title="Preview — variables are resolved per lead on send"
                  style={{ border: "1px solid #e2e7e2", borderRadius: 8, padding: 12, maxHeight: 220, overflowY: "auto", background: "#fafbfa", fontSize: 13 }}
                  dangerouslySetInnerHTML={{ __html: tpl.body_html || "" }}
                />
                {tpl.attach_documents && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, fontSize: 12, color: "#166534" }}>
                    <strong>Pièces jointes :</strong> les documents de chaque lead (devis, factures, fichiers) seront automatiquement attachés à l'email.
                  </div>
                )}
              </>
            );
          })()}
          <p style={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.6 }}>
            One email per recipient: the customer email first, then the lead email. Leads without any email address are skipped and reported.
            Delivery is asynchronous (mail worker, ~every 30 s) — track progress in CRM Settings → Outbox.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 20 }}>
            <button onClick={() => setMailOpen(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={mailBusy || !mailTemplateId} onClick={handleBulkMail} className="ess-btn ess-btn-primary">
              {mailBusy ? "Scheduling…" : `Send to ${selectedIds.length} lead${selectedIds.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </Modal>

      {/* Bulk WhatsApp */}
      <Modal open={waBulkOpen} onClose={() => setWaBulkOpen(false)} title={`Send WhatsApp — ${selectedIds.length} lead${selectedIds.length === 1 ? "" : "s"}`} size="lg">
        <div>
          <FormField label="Template" required>
            {waBulkTemplates.length === 0 ? (
              <div style={{ fontSize: 13, color: "#94a3b8", padding: "9px 0" }}>No WhatsApp templates found. Create one in CRM Settings → Templates.</div>
            ) : (
              <select value={waBulkTemplateId} onChange={(e) => setWaBulkTemplateId(e.target.value)} style={inputStyle}>
                <option value="">Select a template…</option>
                {waBulkTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
          </FormField>
          {(() => {
            const tpl = waBulkTemplates.find((t) => String(t.id) === String(waBulkTemplateId));
            if (!tpl) return null;
            return (
              <div title="Preview — variables are resolved per lead on send" style={{ border: "1px solid #e2e7e2", borderRadius: 8, padding: 12, maxHeight: 220, overflowY: "auto", background: "#f0fdf4", fontSize: 13, color: "#334155", whiteSpace: "pre-wrap" }}>
                {tpl.body_text || "—"}
              </div>
            );
          })()}
          <p style={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.6 }}>
            One WhatsApp message per recipient. Leads without a phone number are skipped and reported.
            Delivery is asynchronous via WhatsApp devices — track progress in CRM Settings → WhatsApp tab.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 20 }}>
            <button onClick={() => setWaBulkOpen(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={waBulkBusy || !waBulkTemplateId} onClick={handleBulkWhatsApp} className="ess-btn ess-btn-primary" style={{ background: "#166534" }}>
              {waBulkBusy ? "Scheduling…" : `Send to ${selectedIds.length} lead${selectedIds.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const viewBtn = {
  display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", fontSize: 13, fontWeight: 500,
  cursor: "pointer", border: "none", background: "transparent",
};

const cardBtn = (color) => ({
  display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 5, fontSize: 11,
  fontWeight: 600, cursor: "pointer", background: "#fff", color, border: `1px solid ${color}33`,
});

const iconBtn = (color) => ({
  border: "1px solid #cbd5e1", background: "white", color, cursor: "pointer", padding: "5px 8px", borderRadius: 6, display: "flex", alignItems: "center",
});

const menuItem = {
  display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 12px", fontSize: 13,
  border: "none", background: "transparent", cursor: "pointer", color: "#334155", textAlign: "left",
  fontFamily: "inherit",
};

const pageBtn = (disabled) => ({
  padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: 6, background: disabled ? "#f1f5f9" : "#fff",
  color: disabled ? "#94a3b8" : "#334155", fontSize: 13, fontWeight: 500, cursor: disabled ? "not-allowed" : "pointer",
});

export default AdminPanelCrmPage;